import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { api } from "../../api";
import { GUIDELINES_ENABLED } from "../../config";
import { Icon, Modal, Empty, Spinner, useToast } from "../../components/ui";
import { SwitchSpinner } from "../../components/SwitchSpinner";
import { uiSwitch } from "../../helpers";
import { derive, raw, applyRefData } from "./recordDerive";
import { AddRecordView } from "./AddRecordView";
import { ImportReconcileGate } from "./ImportReconcileGate";

const SHEETS = [
  { key: "SUBMISSION", label: "Submission" },
  { key: "END_PROCESS", label: "End Process" },
  { key: "MODIFICATION", label: "Modification" },
  { key: "MORE_INFO", label: "More Info Needed" },
];
const SHEET_LABEL = Object.fromEntries(SHEETS.map((s) => [s.key, s.label]));
const STAGE_PROC = { SUBMISSION: "Submitter", END_PROCESS: "Uploader", MODIFICATION: "Modifier", MORE_INFO: "More-Info user" };
const WORKING_GROUPS = ["LACN", "CCCMG"];      // Auth-Needed: assigned & worked directly
const isWorking = (g) => WORKING_GROUPS.includes(g);
const GROUP_ORDER = ["LACN", "CCCMG", "Surgical", "Auth Not Needed", "PHS", "Other"];
const STATUS_COLOR = {
  Approved: "#15803D", Denied: "#B91C1C", Canceled: "#B45309", Withdraw: "#6B7280",
  Pending: "#A16207", Reviewed: "#1D4ED8", "PA Not Required": "#6B7280",
  Faxed: "#7C3AED", "Submitted via call": "#7C3AED",
};
const COLS = [
  { key: "mrn", label: "PID", mono: true, w: "10%" },
  { key: "clinic", label: "Clinic Location", w: "16%" },
  { key: "orders", label: "Order", w: "24%" },
  { key: "urgentRoutine", label: "Urgent", w: "10%", color: (v) => (v === "Urgent" ? "#EA580C" : "") },
  { key: "portal", label: "Portal", w: "12%" },
  { key: "rtcPriority", label: "RTC", w: "9%" },
  { key: "assignedTo", label: "Assigned To", w: "13%" },
  { key: "paStatus", label: "Status", w: "10%", color: (v) => STATUS_COLOR[v] || "" },
];
const stageOf = (r) => r.sheet || "SUBMISSION";
const clinicOf = (r) => (raw(r, "sLocation") || "").toString().trim() || "Unspecified";
// STAT depends only on the record's own fields — compute it directly so the badge/sort never wait on the derived map or refdata.
const isStatRec = (r) => r.priorityOverride === "STAT" || /asap|stat/i.test(String(raw(r, "sDate") || ""));
const groupOf = (r) => r.productionGroup || derive(r).location || "Other";
const isDone = (r) => r.status === "DONE";
const typeOf = (r) => { const g = groupOf(r); if (isWorking(g)) return "Auth Needed"; if (g === "Surgical") return "Surgical"; return "Auth Not Needed"; };
const TYPES = ["Auth Needed", "Surgical", "Auth Not Needed"];
const dnum = (s) => { const t = Date.parse(s); return isNaN(t) ? 0 : t; };

export function Inventory({ user, sheet: sheetProp, initialGroup, lockGroup, groupChoices, issueGroup }) {
  const toast = useToast();
  const isSA = user?.role === "SUPER_ADMIN";
  const canDelete = isSA || user?.role === "ADMIN";
  const canStat = isSA || user?.role === "ADMIN";
  const [allRows, setAllRows] = useState(null);
  const [err, setErr] = useState("");
  const [importErr, setImportErr] = useState("");
  const [uploadBlockMsg, setUploadBlockMsg] = useState("");   // wrong-process upload → blocking modal
  const [importPct, setImportPct] = useState(0);
  const [importPhase, setImportPhase] = useState("");
  const [importName, setImportName] = useState("");
  const [busy, setBusy] = useState(false);
  const [refLoaded, setRefLoaded] = useState(0);
  const [sheetState, setSheetState] = useState("SUBMISSION");
  const sheet = sheetProp || sheetState;
  const setSheet = setSheetState;
  const [group, setGroup] = useState(initialGroup || "All");   // opens on the group chosen from the nav (LACN/CCCMG)
  const [location, setLocation] = useState(null);
  // In the group-scoped view (lockGroup) the grid is the default; Reviewed / Issues are sub-views toggled below the search.
  const [subView, setSubView] = useState("grid");   // grid | reviewed | issues (only used when lockGroup)
  const [statusView, setStatusView] = useState("active");   // active | toassign | inprogress
  // Show the loader and run the switch — only when it's actually a change (skip clicking the active tab).
  // App renders the one centered overlay (uiSwitch); this view no longer paints its own (that was the
  // second, offset spinner on tab switches). The data-driven netLoading keeps the spinner up until the
  // new tab's grid/meta actually arrives.
  const switchView = (isSame, apply) => { if (isSame) { return; } uiSwitch.trigger(); apply(); };
  const [rtcFilter, setRtcFilter] = useState([]);           // RTC-priority filter (multi-select; via the RTC column header)
  const [portalFilter, setPortalFilter] = useState([]);     // Portal filter (multi-select; via the Portal column header)
  const [headerFilter, setHeaderFilter] = useState(null);   // which column's filter dialog is open
  const [assignFilter, setAssignFilter] = useState([]);     // Assigned To filter (user ids / "__NONE__")
  const [orderFilter, setOrderFilter] = useState([]);       // Order filter (order names)
  const [orderMdFilter, setOrderMdFilter] = useState([]);   // Order MD filter (Excluded MD tab only)
  const [clinicFilter, setClinicFilter] = useState([]);     // Clinic Location filter (clinic names)
  const [statusFilter, setStatusFilter] = useState([]);     // Status filter (paStatus values / "__NONE__")
  const [mapPortalRec, setMapPortalRec] = useState(null);   // record whose payor we're mapping to a portal
  const [portalsList, setPortalsList] = useState([]);       // all portals (for the map dialog)
  const [pageIdx, setPageIdx] = useState(0);                 // client-side pagination
  const [pageSize, setPageSize] = useState(75);             // rows per page (user-selectable)
  const [viewing, setViewing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [menu, setMenu] = useState(null);
  const [delRec, setDelRec] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [archiveReq, setArchiveReq] = useState(null);   // {ids}
  const [reassignReq, setReassignReq] = useState(null); // {uid, ids, busy}
  const [unassignReq, setUnassignReq] = useState(null); // {ids, recs}
  const [archiveReason, setArchiveReason] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(null);   // { key, dir } or null = default
  const uploadSheet = useRef("SUBMISSION");
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const [cursor, setCursor] = useState(-1);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(600);
  const ROW_H = 41;

  // reference data (locations/payors/portals) loads once, not on every sheet switch
  useEffect(() => { api.getRefData().then((ref) => { applyRefData(ref); setRefLoaded((n) => n + 1); }).catch(() => {}); }, []);
  // portals list for the right-click "Map portal" dialog (loaded once)
  useEffect(() => { api.listPortals().then((ps) => setPortalsList(ps || [])).catch(() => {}); }, []);

  const [totalCount, setTotalCount] = useState(0); // total for this sheet+group+filters (from the server page)
  const [meta, setMeta] = useState(null);          // { groups, groupsDone, archive, duplicates, unassignedStat, rtcOptions }
  const [pageUnassignedStat, setPageUnassignedStat] = useState(0); // fast RTC-lock signal (comes with each grid page)
  const loadSeq = useRef(0);
  const metaSeq = useRef(0);

  // debounce the search box so we don't hit the server on every keystroke
  const [qDebounced, setQDebounced] = useState("");
  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 300); return () => clearTimeout(t); }, [q]);

  // fetch the current page from the server (rows are filtered + sorted + paginated in the DB)
  // Effective query group for the group-scoped sub-views (each scoped to this group via :issueGroup).
  const qGroup = subView === "issues" ? "__ISSUES__"
    : subView === "rework" ? "__REWORK__"
    : subView === "authnot" ? "__AUTHNOT__"
    : subView === "exclmd" ? "__EXCLMD__"
    : subView === "surg" ? "__SURG__"
    : group;
  const qIssue = (subView === "issues" || subView === "rework" || subView === "authnot" || subView === "exclmd" || subView === "surg") ? group : issueGroup;
  // Column layout: Excluded MD adds the Order MD column; Auth Not Needed drops Assigned/Status and shows Payor.
  const exclActive = group === "Excluded MD" || subView === "exclmd";
  const authNotActive = group === "Auth Not Needed" || subView === "authnot";
  const surgActive = group === "Surgical" || subView === "surg";   // surgical records are moved to production, not assigned
  const loadPage = useCallback(async () => {
    if (subView === "reviewed") return;   // reviewed sub-view fetches its own data (not the records grid)
    setErr("");
    const mySeq = ++loadSeq.current;
    try {
      const res = await api.grid({ sheet, group: qGroup, location, status: statusView, rtc: rtcFilter.join(","), portal: portalFilter.join(","), assignedTo: assignFilter.join(","), orders: orderFilter.join("\u001F"), clinics: clinicFilter.join("\u001F"), paStatuses: statusFilter.join(","), orderMds: exclActive ? orderMdFilter.join("\u001F") : "", issueGroup: (qGroup === "__ISSUES__" || qGroup === "__REWORK__" || qGroup === "__AUTHNOT__" || qGroup === "__EXCLMD__") ? (qIssue || "") : "", q: qDebounced, page: pageIdx, size: pageSize });
      if (loadSeq.current !== mySeq) return;
      setAllRows(res.content || []);
      setTotalCount(res.total || 0);
      setPageUnassignedStat(res.unassignedStat ?? 0);
    } catch (e) { if (loadSeq.current === mySeq) { setErr(e.message); setAllRows([]); } }
  }, [sheet, qGroup, qIssue, subView, location, statusView, rtcFilter.join(","), portalFilter.join(","), assignFilter.join(","), orderFilter.join(","), orderMdFilter.join(","), clinicFilter.join(","), statusFilter.join(","), qDebounced, pageIdx, pageSize]); // eslint-disable-line
  useEffect(() => { loadPage(); }, [loadPage]);
  useEffect(() => { setAllRows(null); }, [sheet]);   // clear the grid while a new sheet's first page loads

  // fetch the toolbar meta (tab counts, archive/dup, RTC options + the "Assigned today" board)
  // for the current sheet + group. Guarded by a sequence number so that when several refreshes
  // overlap (after an action, on tab focus, and the 15s timer), only the LATEST response is
  // applied — an older/slower one can no longer land last and drop users from the board.
  const loadMeta = useCallback(async (silent = false) => {
    const mySeq = ++metaSeq.current;
    try {
      // pass the current status view + location so the RTC/Portal filter option counts match what the grid shows.
      // optGroup scopes the filter dropdowns to the current sub-view so they list only that tab's values.
      const optGroup = subView === "exclmd" ? "Excluded MD" : subView === "authnot" ? "Auth Not Needed" : subView === "surg" ? "Surgical" : subView === "rework" ? "__REWORK__" : null;
      // Active column filters → the option lists cascade (each column narrows to the OTHER filters).
      // Same encoding the grid uses (orders/clinics/orderMds joined by Unit Separator, others by comma).
      const filters = { rtc: rtcFilter.join(","), portal: portalFilter.join(","), assignedTo: assignFilter.join(","), orders: orderFilter.join("\u001F"), clinics: clinicFilter.join("\u001F"), paStatuses: statusFilter.join(","), orderMds: exclActive ? orderMdFilter.join("\u001F") : "", q: qDebounced };
      const m = await api.gridMeta(sheet, group, statusView, location, optGroup, filters, silent ? { silent: true } : undefined);
      if (metaSeq.current === mySeq) setMeta(m);
    } catch { /* keep old meta */ }
  }, [sheet, group, statusView, location, subView, exclActive, rtcFilter.join(","), portalFilter.join(","), assignFilter.join(","), orderFilter.join("\u001F"), clinicFilter.join("\u001F"), statusFilter.join(","), orderMdFilter.join("\u001F"), qDebounced]); // eslint-disable-line
  useEffect(() => { loadMeta(); }, [loadMeta]);

  // one call to refresh everything after an action or import
  const load = useCallback(() => { loadPage(); loadMeta(); }, [loadPage, loadMeta]);

  // Keep the "Assigned today" board (and the tab counts it shares) current WITHOUT a manual
  // browser refresh: re-pull meta whenever the tab regains focus/visibility, and on a light
  // 15s timer while the tab is visible. This is why assignments now appear on their own.
  useEffect(() => {
    // Don't refresh while a column-filter dialog is open — it would swap the portal/RTC options
    // out from under the dialog (the "sometimes shows portals, sometimes doesn't" flicker).
    const refresh = () => { if (!document.hidden && !headerFilter) loadMeta(true); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const id = setInterval(refresh, 15000);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      clearInterval(id);
    };
  }, [loadMeta, headerFilter]);

  useEffect(() => { api.listAssignees(sheet).then(setAssignees).catch(() => setAssignees([])); }, [sheet]);
  useEffect(() => { setSelected(new Set()); if (scrollRef.current) scrollRef.current.scrollTop = 0; setScrollTop(0); }, [sheet, group, location]);
  useEffect(() => { setPageIdx(0); }, [sheet, group, location, statusView, q, rtcFilter.join(","), portalFilter.join(","), assignFilter.join(","), orderFilter.join(","), orderMdFilter.join(","), clinicFilter.join(","), statusFilter.join(","), pageSize]); // eslint-disable-line
  // switching sub-view (grid / reviewed / issues) resets paging, selection and the status view
  useEffect(() => { setPageIdx(0); setSelected(new Set()); setStatusView("active"); }, [subView]);
  // The Order MD filter only exists on the Excluded MD tab/view — clear it when leaving.
  useEffect(() => { if (!exclActive && orderMdFilter.length) setOrderMdFilter([]); }, [exclActive]); // eslint-disable-line

  const sheetRows = useMemo(() => (allRows || []), [allRows]); // server already returns this sheet's page (filtered + sorted)
  // Remember the actual record objects for every selection, across pages (server pagination only keeps
  // the current page in sheetRows). This lets Assign include selections made on other pages.
  const [selMeta, setSelMeta] = useState(() => new Map());
  useEffect(() => {
    setSelMeta((prev) => {
      const m = new Map(prev);
      for (const r of sheetRows) if (selected.has(r.id)) m.set(r.id, r);
      for (const id of [...m.keys()]) if (!selected.has(id)) m.delete(id);
      return m;
    });
  }, [selected, sheetRows]);
  const selectedAll = [...selected].map((id) => selMeta.get(id)).filter(Boolean);

  const isNormalTab = qGroup !== "__ARCHIVE__" && qGroup !== "__DUPLICATE__" && qGroup !== "__ISSUES__" && qGroup !== "__REWORK__";
  const isReworkTab = qGroup === "__REWORK__";

  // Whether today's import has already run — from meta (whole sheet), falling back to the current page.
  const importedToday = useMemo(() => {
    if (meta && typeof meta.importedToday === "boolean") return meta.importedToday;
    const n = new Date();
    return (allRows || []).some((r) => {
      if (!r.createdAt) return false; const c = new Date(r.createdAt);
      return c.getFullYear() === n.getFullYear() && c.getMonth() === n.getMonth() && c.getDate() === n.getDate();
    });
  }, [meta, allRows]);

  // reset the column filters when the sheet/group context changes (options differ per context)
  useEffect(() => { setRtcFilter([]); setPortalFilter([]); setAssignFilter([]); setOrderFilter([]); setClinicFilter([]); setStatusFilter([]); setHeaderFilter(null); }, [sheet, group]);
  // switching group tabs always drops back to the "All active" status view (To assign / Allocated / Today's work
  // are per-group states that shouldn't carry over to another group).
  useEffect(() => { setStatusView("active"); }, [group]);

  // group tabs + clinic sub-menus come from meta (whole sheet), not the current page
  // Group tabs are fixed — always show every group regardless of what the current sheet contains.
  const groupsPresent = useMemo(() => (groupChoices && groupChoices.length) ? groupChoices : ["LACN", "CCCMG", "Surgical", "Auth Not Needed", "PHS", "Excluded MD"], [groupChoices]);

  const archiveCount = meta?.archive ?? 0;
  const dupCount = meta?.duplicates ?? 0;
  const issuesCount = meta?.issues ?? 0;
  const reworkedCount = meta?.reworked ?? 0;
  const [reviewedTabCount, setReviewedTabCount] = useState(0);   // count of kept-in-Reviewed records, reported by ReviewedPane
  const isIssuesTab = qGroup === "__ISSUES__";
  const sheetTotal = useMemo(() => {
    const g = meta?.groups || {};
    return Object.values(g).reduce((s, n) => s + (Number(n) || 0), 0) + (meta?.archive || 0) + (meta?.duplicates || 0);
  }, [meta]);
  const isArchiveTab = group === "__ARCHIVE__";
  const isDupTab = group === "__DUPLICATE__";

  // The server already filtered + sorted this page; the client just renders it.
  const rows = sheetRows;

  // default to the first present group (keep special tabs sticky while they have items)
  useEffect(() => {
    if (lockGroup) return;   // group is fixed by the caller (group-scoped / issues view) — never auto-switch
    if (isArchiveTab && archiveCount > 0) return;
    if (isDupTab && dupCount > 0) return;
    if (isIssuesTab && issuesCount > 0) return;
    if (isArchiveTab || isDupTab || isIssuesTab || group === "All" || !groupsPresent.includes(group)) {
      if (groupsPresent.length) setGroup(groupsPresent[0]);
    }
  }, [groupsPresent, group, archiveCount, dupCount, issuesCount]); // eslint-disable-line
  const derived = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const d = derive(r);
      // Show the STORED RTC band (what the filter uses) so the RTC column matches the RTC filter exactly.
      m.set(r.id, { ...d, rtcPriority: (r.rtcPriority != null ? r.rtcPriority : d.rtcPriority), assignedTo: r.assignedUserCas || r.assignedUserName || "", paStatus: r.paStatus || "" });
    });
    return m;
  }, [rows, refLoaded]); // eslint-disable-line
  const sorted = useMemo(() => {
    if (!sort) return rows;   // server already returns the page STAT-first (prioritized → STAT → urgent → RTC)
    const dv = (r, k) => { const d = derived.get(r.id) || {}; return d[k]; };
    const dir = sort.dir === "desc" ? -1 : 1;
    const isDate = sort.key === "rtcDate";
    // STAT and prioritized records ALWAYS stay on top; the column sort only orders within them.
    const rank = (r) => (r.prioritized ? 2 : 0) + (isStatRec(r) ? 1 : 0);
    return [...rows].sort((a, b) => {
      const rd = rank(b) - rank(a);
      if (rd) return rd;
      const va = dv(a, sort.key), vb = dv(b, sort.key);
      if (isDate) return (dnum(va) - dnum(vb)) * dir;
      return String(va || "").localeCompare(String(vb || ""), undefined, { numeric: true }) * dir;
    });
  }, [rows, derived, sort]);

  const summary = useMemo(() => {
    const m = { "Auth Needed": { open: 0, done: 0 }, Surgical: { open: 0, done: 0 }, "Auth Not Needed": { open: 0, done: 0 } };
    const typeForGroup = (g) => g === "Surgical" ? "Surgical" : g === "Auth Not Needed" ? "Auth Not Needed" : "Auth Needed";
    const open = meta?.groups || {}, done = meta?.groupsDone || {};
    Object.entries(open).forEach(([g, n]) => { m[typeForGroup(g)].open += Number(n) || 0; });
    Object.entries(done).forEach(([g, n]) => { m[typeForGroup(g)].done += Number(n) || 0; });
    return m;
  }, [meta]);

  // selection helpers
  const ids = sorted.map((r) => r.id);

  // virtualization: keep the viewport height measured reliably (fixes the occasional blank grid)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setViewH(el.clientHeight || 600);
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(measure); ro.observe(el); }
    window.addEventListener("resize", measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [allRows, sheet]);
  const total = totalCount;              // total for the current group/filters (from the server)
  const PAGE_SIZE = pageSize;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageStart = pageIdx * PAGE_SIZE;
  const visibleRows = sorted;            // server already returned exactly this page
  useEffect(() => { setPageIdx((p) => Math.min(p, Math.max(0, pageCount - 1))); }, [pageCount]);

  const allSel = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSel = ids.some((id) => selected.has(id));
  const anchorRef = useRef(-1);
  const pendingScroll = useRef(null);
  const scrollToRow = (id) => { const el = scrollRef.current && scrollRef.current.querySelector(`[data-rid="${id}"]`); if (el) el.scrollIntoView({ block: "nearest" }); };
  useEffect(() => { if (pendingScroll.current != null) { scrollToRow(pendingScroll.current); pendingScroll.current = null; } });

  const toggleOne = (id) => {
    setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const idx = sorted.findIndex((x) => x.id === id); setCursor(idx); anchorRef.current = idx;
  };

  // Arrow up/down moves the cursor and selects; Shift+Arrow selects a range. The grid scrolls to follow.
  const onGridKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    if (!sorted.length) return;
    e.preventDefault();
    const cur = cursor < 0 ? 0 : cursor;   // cursor is an index within the current page
    const next = e.key === "ArrowDown" ? Math.min(cur + 1, sorted.length - 1) : Math.max(cur - 1, 0);
    const nextId = sorted[next].id;
    if (e.shiftKey) {
      const a = anchorRef.current < 0 ? cur : anchorRef.current;
      anchorRef.current = a;
      const lo = Math.min(a, next), hi = Math.max(a, next);
      setSelected(new Set(sorted.slice(lo, hi + 1).map((r) => r.id)));
    } else {
      anchorRef.current = next;
      setSelected((p) => { const n = new Set(p); n.add(nextId); return n; });
    }
    setCursor(next);
    pendingScroll.current = nextId;                     // scroll after the (re)render
  };
  const selectAll = () => setSelected(allSel ? new Set() : new Set(ids));
  const clearSel = () => setSelected(new Set());
  const selRecords = sorted.filter((r) => selected.has(r.id));
  // Assign vs Move-to-production is decided by the CURRENT group tab (the server-side group of every row
  // shown here), not by a per-row client re-derive — those can disagree until a Recompute runs.
  // Auth Not Needed / Excluded MD / Surgical records are moved to production first (never assigned here), even inside a working group.
  // Reworked records (worked before, then flipped back to "auth needed" in Onco EMR) are re-allocated to a user,
  // so the Reworked tab behaves like a working tab for selection — Assign…, not Move to production.
  const selAllWorking = selRecords.length > 0 && (isReworkTab || (isNormalTab && isWorking(group) && !authNotActive && !exclActive && !surgActive));
  const selAllNonWorking = selRecords.length > 0 && isNormalTab && (!isWorking(group) || authNotActive || exclActive || surgActive);

  const [phiPrompt, setPhiPrompt] = useState(false);
  const triggerUpload = (s, e) => { e.stopPropagation(); uploadSheet.current = s; setImportErr(""); setPhiPrompt(true); };
  const proceedToFile = () => { setPhiPrompt(false); fileRef.current?.click(); };
  const importFileRef = useRef(null);
  const importXhr = useRef(null);
  const importToken = useRef(null);
  const [cancelAsk, setCancelAsk] = useState(false);
  const [conflicts, setConflicts] = useState(null);   // { list, sheet } when confirmation needed
  const runImport = async (file, sheet, forceUids) => {
    setImportErr(""); setBusy(true); setImportPct(0); setImportPhase("uploading"); setImportName(file.name);
    const token = `imp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    importToken.current = token;
    try {
      const res = await api.importPatientRecordsProgress(file, sheet, (pct, phase) => { setImportPct(pct); setImportPhase(phase); }, forceUids,
        (xhr) => { importXhr.current = xhr; }, token);
      if (!forceUids && res?.conflicts?.length) {
        importFileRef.current = file;
        setConflicts({ list: res.conflicts, sheet, importedMsg: res.message });
      } else {
        toast(res?.message || "Import complete", "ok");
      }
      load();
    } catch (ex) {
      // server rolls back on cancel and returns a 400 with this message; also handle the XHR abort case
      if (ex.cancelled || /cancelled/i.test(ex.message || "")) toast("Import cancelled — no records were imported", "info");
      // wrong-process upload → block with a modal the admin must acknowledge before continuing
      else if (/rename the file|doesn't look like a/i.test(ex.message || "")) setUploadBlockMsg(ex.message);
      else setImportErr(ex.message);
    }
    finally { importXhr.current = null; importToken.current = null; setBusy(false); setImportPct(0); setImportPhase(""); setImportName(""); setCancelAsk(false); }
  };
  // Tell the server to cancel (it rolls back so nothing saves), then abort the upload.
  const cancelImport = () => {
    if (importToken.current) { api.cancelImport(importToken.current).catch(() => {}); }
    if (importXhr.current) { try { importXhr.current.abort(); } catch {} }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    runImport(file, uploadSheet.current, null);
  };
  const confirmConflicts = async (uids) => {
    const file = importFileRef.current; const sheet = conflicts.sheet;
    setConflicts(null);
    if (uids.length && file) await runImport(file, sheet, uids);
    importFileRef.current = null;
  };
  const act = async (fn, okMsg) => {
    setMenu(null);
    try { const res = await fn(); if (okMsg) toast(typeof okMsg === "function" ? okMsg(res) : okMsg, "ok"); clearSel(); load(); }
    catch (ex) { toast(ex.message, "err"); }
  };
  // Recompute duplicate / reworked / derived flags across all records — fixes records stuck in the wrong tab
  // (e.g. a fresh reworked copy hidden behind its auto-archived old copy).
  const [recomputing, setRecomputing] = useState(false);
  const doRecompute = async () => {
    if (recomputing) return;
    setRecomputing(true);
    try { const r = await api.recomputeDerived(); toast(`Recompute complete — ${(r?.updated ?? 0).toLocaleString()} records refreshed`, "ok"); load(); }
    catch (ex) { toast(ex.message, "err"); }
    finally { setRecomputing(false); }
  };
  // context-menu target: the selection if the clicked row is part of it, else just that row
  const menuIds = () => (menu && selected.has(menu.record.id) && selected.size ? [...selected] : menu ? [menu.record.id] : []);
  const menuRecords = () => { const ids = new Set(menuIds()); return sheetRows.filter((r) => ids.has(r.id)); };

  const bulkAssign = () => { if (!assignTo || !selected.size) return; act(() => api.assignRecords([...selected], Number(assignTo)), (r) => `Assigned ${r.assigned}`); setAssignTo(""); };
  const prodGroupFor = (recs) => (recs.length && recs.every((r) => derive(r).clinicGroup === "CCCMG")) ? "CCCMG" : "LACN";
  const bulkMoveProd = () => { const recs = sheetRows.filter((r) => selected.has(r.id)); const g = prodGroupFor(recs); act(() => api.sendToProduction([...selected], g), "Moved to production → " + g); };
  const proc = STAGE_PROC[sheet];

  // Status column only makes sense where you look at outcomes: All active + In progress.
  // To assign (assignment only) and Allocated (before work) don't need it.
  const showStatus = statusView === "active" || statusView === "todays";
  let cols = showStatus ? COLS : COLS.filter((c) => c.key !== "paStatus");
  // Excluded MD tab gets an extra, filterable "Order MD" column (that's the column the exclusion is based on).
  if (exclActive) cols = [...cols.slice(0, 3), { key: "orderMD", label: "Order MD", w: "14%" }, ...cols.slice(3)];
  // Auth Not Needed: these records aren't assigned/worked here (you move them to production first, then assign
  // later if you want), so drop the Assigned To and Status columns and show the Payor instead.
  if (authNotActive) {
    cols = COLS.filter((c) => c.key !== "assignedTo" && c.key !== "paStatus");
    const payorCol = { key: "insurance", label: "Payor", w: "16%" };
    const pIdx = cols.findIndex((c) => c.key === "portal");
    cols = pIdx >= 0 ? [...cols.slice(0, pIdx + 1), payorCol, ...cols.slice(pIdx + 1)] : [...cols, payorCol];
  }

  if (adding || editRec) {
    return <AddRecordView sheet={sheet} assignees={assignees} editRecord={editRec}
      allowedGroups={lockGroup ? [group] : (groupChoices || null)}
      onClose={() => { setAdding(false); setEditRec(null); }}
      onCreated={() => { setAdding(false); setEditRec(null); load(); }} />;
  }
  if (allRows === null) return <div className="empty"><Spinner /></div>;
  if (err) return <div className="err-text" style={{ padding: 18 }}>{err}</div>;

  return (
    <div className="inv-layout">
      {/* Daily import value-reconciliation gate — blocks allocation for admins until changed values are cleared. */}
      <ImportReconcileGate user={user} />
      {busy && (
        <div className={"import-overlay" + (cancelAsk ? " paused" : "")}>
          <div className="import-overlay-box">
            <div className={"import-spinner" + (cancelAsk ? " paused" : "")} />
            <div className="import-overlay-title">
              {cancelAsk ? "Import paused"
                : importPhase === "processing" ? "Processing records on the server…"
                : `Uploading${importName ? ` “${importName}”` : ""} — ${importPct}%`}
            </div>
            <div className="import-overlay-bar">
              <div className={"import-overlay-fill" + (!cancelAsk && importPhase === "processing" ? " indeterminate" : "")}
                style={cancelAsk ? { width: "100%", opacity: .4 } : importPhase === "processing" ? undefined : { width: importPct + "%" }} />
            </div>
            <div className="import-overlay-hint">{cancelAsk ? "Nothing is being saved while paused. Choose below to cancel or continue." : "Please keep this window open and don't switch tabs until the import finishes."}</div>
            {!cancelAsk ? (
              <button className="btn btn-ghost import-cancel-btn" onClick={() => { setCancelAsk(true); if (importToken.current) api.pauseImport(importToken.current).catch(() => {}); }}>Cancel import</button>
            ) : (
              <div className="import-cancel-confirm">
                <div className="import-cancel-q"><b>Import paused</b> — it won't finish until you choose. Cancel it? <b>No records will be imported.</b></div>
                <div className="import-cancel-actions">
                  <button className="btn btn-ghost" onClick={() => { if (importToken.current) api.resumeImport(importToken.current).catch(() => {}); setCancelAsk(false); }}>Keep importing</button>
                  <button className="btn btn-danger" onClick={cancelImport}>Yes, cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div>
        {/* Line 1: heading · search · hint · status view · Add new record (last). */}
        <div className="inv-head">
          <h2 className="inv-title">{SHEET_LABEL[sheet]}</h2>
          <div className="inv-search">
            <Icon.search width="15" height="15" />
            <input className="inp" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} title="Search by PID (MRN), order name, or the assigned user's name" />
            {q && <button className="ds-clear" onClick={() => setQ("")}><Icon.x width="13" height="13" /></button>}
          </div>
          <span className="search-hint">Search by PID, order or user</span>
          <div className="spacer" />
          {/* Status view — always shown; locked (disabled) on the Reviewed sub-view so it doesn't disappear. */}
          <div className="status-seg">
            {[["active", "All active"], ["toassign", "To assign"], ["allocated", "Allocated"]].map(([k, label]) => (
              <button key={k} className={"seg-btn" + (statusView === k ? " on" : "")} disabled={subView === "reviewed"}
                onClick={() => { if (subView !== "reviewed") switchView(statusView === k, () => setStatusView(k)); }}>{label}</button>
            ))}
          </div>
          {subView === "grid" && (group === "LACN" || group === "CCCMG") && (sheet === "SUBMISSION" || sheet === "END_PROCESS") && (
            <button className="btn btn-ghost btn-sm" onClick={() => setAdding(true)} disabled={busy}>
              <Icon.plus width="15" height="15" />Add new record
            </button>
          )}
        </div>
        {/* Line 2 (group-scoped view): separate, non-joined pills. The grid (Records) is the default. */}
        {lockGroup && (
          <div className="gi-tabs">
            <button className={"gpill" + (subView === "grid" ? " on" : "")} onClick={() => switchView(subView === "grid", () => setSubView("grid"))}>{group}</button>
            <button className={"gpill" + (subView === "reviewed" ? " on" : "")} onClick={() => switchView(subView === "reviewed", () => setSubView("reviewed"))}>Reviewed{reviewedTabCount > 0 ? ` (${reviewedTabCount})` : ""}</button>
            <button className={"gpill" + (subView === "issues" ? " on" : "")} onClick={() => switchView(subView === "issues", () => setSubView("issues"))}>⚠️ Issues</button>
            <button className={"gpill" + (subView === "authnot" ? " on" : "")} onClick={() => switchView(subView === "authnot", () => setSubView("authnot"))}>Auth Not Needed</button>
            <button className={"gpill" + (subView === "exclmd" ? " on" : "")} onClick={() => switchView(subView === "exclmd", () => setSubView("exclmd"))}>Excluded MD</button>
            <button className={"gpill" + (subView === "surg" ? " on" : "")} onClick={() => switchView(subView === "surg", () => setSubView("surg"))}>Surgical</button>
            <button className={"gpill" + (subView === "rework" ? " on" : "")} onClick={() => switchView(subView === "rework", () => setSubView("rework"))}>Reworked{reworkedCount > 0 ? ` (${reworkedCount})` : ""}</button>
          </div>
        )}
        <input ref={fileRef} type="file" accept=".csv,.xlsx" hidden onChange={onFile} />
        {importErr && <div className="import-err"><Icon.x width="15" height="15" /><span>{importErr}</span><button className="phi-close" onClick={() => setImportErr("")}><Icon.x width="13" height="13" /></button></div>}
        {busy && (
          <div className="import-prog">
            <div className="import-prog-top">
              <span className="spin" />
              <span className="import-prog-label">
                {importPhase === "processing"
                  ? <>Uploaded — processing records on the server…</>
                  : <>Uploading {importName ? `“${importName}”` : "file"} — {importPct}%</>}
              </span>
            </div>
            <div className="import-bar">
              <div className={"import-bar-fill" + (importPhase === "processing" ? " indeterminate" : "")}
                style={importPhase === "processing" ? undefined : { width: importPct + "%" }} />
            </div>
            <div className="import-prog-hint">{importPhase === "processing" ? "Large sheets can take a little while — please keep this open." : "Sending the file…"}</div>
          </div>
        )}

        {/* Group pills row — only when the caller isn't locking the group (the group-scoped view has its own tabs). */}
        {!lockGroup && (
        <div className="inv-filter-row">
          <div className="group-pills">
            {groupsPresent.map((g) => (
              <div className="gpill-wrap" key={g}>
                <button className={"gpill" + (g === group ? " on" : "")} onClick={() => switchView(g === group, () => setGroup(g))}>{g}</button>
              </div>
            ))}
            {!groupChoices && issuesCount > 0 && (
              <div className="gpill-wrap">
                <button className={"gpill" + (isIssuesTab ? " on" : "")} title="Issues — records flagged as known issues"
                  onClick={() => switchView(group === "__ISSUES__", () => { setGroup("__ISSUES__"); setLocation(null); })}>⚠️ Issues</button>
              </div>
            )}
            {dupCount > 0 && (
              <div className="gpill-wrap">
                <button className={"gpill" + (isDupTab ? " on" : "")} onClick={() => switchView(isDupTab, () => { setGroup("__DUPLICATE__"); setLocation(null); })}>Duplicates</button>
              </div>
            )}
            {archiveCount > 0 && (
              <div className="gpill-wrap">
                <button className={"gpill gpill-arch-sm" + (isArchiveTab ? " on" : "")} onClick={() => switchView(isArchiveTab, () => { setGroup("__ARCHIVE__"); setLocation(null); })}>Archive <span className="gpill-n">{archiveCount}</span></button>
              </div>
            )}
          </div>
        </div>
        )}

        {selected.size > 0 && !assignOpen && (
          <div className="sel-fab">
            <span className="sel-count">{selected.size} selected</span>
            <button className="link-btn" onClick={clearSel}>Clear</button>
            <div className="sel-sep" />
            {selAllWorking && <button className="btn btn-primary btn-sm" onClick={() => { setAssignTo(""); setAssignOpen(true); }}>Assign…</button>}
            {selAllNonWorking && <button className="btn btn-primary btn-sm" onClick={bulkMoveProd}>Move to production</button>}
            {!selAllWorking && !selAllNonWorking && <span className="assign-none">Pick one group to assign</span>}
          </div>
        )}

        {subView === "reviewed" ? <ReviewedPane sheet={sheet} group={group} q={q} onCount={setReviewedTabCount} onOpen={(rec) => setViewing(rec)} /> : (
        <div className="panel" style={{ paddingTop: 0 }}>
              <div className="table-wrap no-scroll" ref={scrollRef} tabIndex={0} onKeyDown={onGridKey}
                onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}>
                <table className="tbl select-tbl fit-tbl virt">
                  <colgroup>
                    <col style={{ width: "40px" }} />
                    {cols.map((c) => <col key={c.key} style={{ width: c.w }} />)}
                  </colgroup>
                  <thead><tr>
                    <th className="col-check">{(isDupTab || isIssuesTab) ? null : <label className="cbx"><input type="checkbox" checked={allSel} ref={(el) => { if (el) el.indeterminate = !allSel && someSel; }} onChange={selectAll} /><span className="box"><Icon.check /></span></label>}</th>
                    {cols.map((c) => {
                      const FILTERABLE = ["portal", "rtcPriority", "assignedTo", "orders", "orderMD", "clinic", "paStatus"];
                      const filterable = FILTERABLE.includes(c.key);   // filters on every tab, incl. Reworked
                      const activeCount = c.key === "portal" ? portalFilter.length : c.key === "rtcPriority" ? rtcFilter.length
                        : c.key === "assignedTo" ? assignFilter.length : c.key === "orders" ? orderFilter.length : c.key === "orderMD" ? orderMdFilter.length : c.key === "clinic" ? clinicFilter.length : c.key === "paStatus" ? statusFilter.length : 0;
                      if (filterable) {
                        return (
                          <th key={c.key} className={"th-sort th-filter" + (activeCount ? " th-filter-on" : "")}
                            onClick={(e) => { e.stopPropagation(); setHeaderFilter(headerFilter === c.key ? null : c.key); }}
                            title={`Filter by ${c.label}`}>
                            {c.label}
                            <span style={{ marginLeft: 5, fontSize: 11, opacity: activeCount ? 1 : 0.55 }}>▾</span>
                            {activeCount > 0 && <span style={{ marginLeft: 4, background: "#4f46e5", color: "#fff", borderRadius: 999, padding: "0 6px", fontSize: 10.5, fontWeight: 800 }}>{activeCount}</span>}
                          </th>
                        );
                      }
                      return (
                        <th key={c.key} className="th-sort" onClick={() => setSort((s) => s && s.key === c.key ? (s.dir === "asc" ? { key: c.key, dir: "desc" } : null) : { key: c.key, dir: "asc" })}>
                          {c.label}{sort && sort.key === c.key && <span className="th-caret">{sort.dir === "asc" ? " ▲" : " ▼"}</span>}
                        </th>
                      );
                    })}
                  </tr></thead>
                  <tbody>
                    {visibleRows.map((r) => {
                      const d = derived.get(r.id) || {}; const isSel = selected.has(r.id);
                      return (
                        <tr key={r.id} data-rid={r.id}
                          className={"row-click" + (isSel ? " selected" : "") + (r.archiveStatus === "REQUESTED" ? " arch-row" : "") + (isDupTab ? " dup-row" : "") + (r.prioritized && !isDupTab ? " prio-row" : "") + (r.issue ? " issue-row" : "")}
                          style={r.issue ? { background: "#fee2e2", boxShadow: "inset 3px 0 0 #dc2626" } : undefined}
                          onClick={() => setViewing(r)}
                          onContextMenu={(e) => { if (isDupTab) return; e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, record: r }); }}>
                          <td className="col-check" onClick={(e) => e.stopPropagation()}>
                            {r.issue && <span title={"Known issue — do not work" + (r.issueGroup ? " (" + r.issueGroup + ")" : "")} style={{ position: "absolute", bottom: 0, left: 0, background: "#dc2626", color: "#fff", fontSize: 8, fontWeight: 800, letterSpacing: ".02em", lineHeight: 1.4, padding: "0 5px", borderRadius: "0 6px 0 4px", zIndex: 2, whiteSpace: "nowrap" }}>ISSUE</span>}
                            {!isDupTab && r.priorityOverride === "STAT" && <span className="stat-corner" title="Manually made STAT (was Non-STAT)"><span style={{ color: "#facc15" }}>M</span> STAT</span>}
                            {!isDupTab && r.priorityOverride !== "STAT" && isStatRec(r) && <span className="stat-corner" title="STAT priority">STAT</span>}
                            {!isDupTab && r.prioritized && <span className="prio-corner" title="Prioritized — high priority">★</span>}
                            {r.manualEntry && <span className="manual-corner" title="Manually added record">M</span>}
                            {r.archiveStatus === "REQUESTED" && <span className="arch-badge-sm" title="Archive requested — awaiting super admin">arch req</span>}
                            {r.reworkedOut && <span className="arch-badge-sm" style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" }} title="Superseded by a re-import in Onco EMR — auto-archived">Reworked</span>}
                            {(isDupTab || isIssuesTab)
                              ? null
                              : <label className="cbx"><input type="checkbox" checked={isSel} onChange={() => toggleOne(r.id)} /><span className="box"><Icon.check /></span></label>}
                          </td>
                          {cols.map((c) => {
                            const v = d[c.key];
                            // In "Today's work", an allocated-but-unworked record shows "Not worked" in the Status column.
                            if (c.key === "paStatus" && statusView === "todays" && (v === "" || v == null)) {
                              return <td key={c.key} className="wrap-cell"><span style={{ color: "#94a3b8", fontStyle: "italic" }}>Not worked</span></td>;
                            }
                            if (c.color) { const col = c.color(v); return <td key={c.key} className="wrap-cell">{v ? <span style={col ? { color: col, fontWeight: 700 } : undefined}>{v}</span> : "—"}</td>; }
                            // Assigned To shows the CAS short name; on hover show the person's full name.
                            const cellTitle = c.key === "assignedTo" ? (r.assignedUserName || v || "") : (v || "");
                            return <td key={c.key} className={(c.mono ? "mono " : "cell-sub ") + "wrap-cell"} title={cellTitle}>{v === "" || v == null ? "—" : v}</td>;
                          })}
                        </tr>
                      );
                    })}
                    {sorted.length === 0 && (
                      <tr>
                        <td colSpan={cols.length + 1} style={{ textAlign: "center", color: "#64748b", padding: "40px 16px" }}>
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>No records match the current filters.</div>
                          <div style={{ fontSize: 12.5 }}>
                            {(rtcFilter.length || portalFilter.length || assignFilter.length || orderFilter.length || orderMdFilter.length || clinicFilter.length || statusFilter.length || q)
                              ? "Try clearing the column filters or search."
                              : `No records in ${SHEET_LABEL[sheet]} yet — use Upload to add them.`}
                          </div>
                          {(rtcFilter.length || portalFilter.length || assignFilter.length || orderFilter.length || orderMdFilter.length || clinicFilter.length || statusFilter.length) > 0 && (
                            <button className="link-btn" style={{ marginTop: 8 }} onClick={() => { setRtcFilter([]); setPortalFilter([]); setAssignFilter([]); setOrderFilter([]); setOrderMdFilter([]); setClinicFilter([]); setStatusFilter([]); }}>Clear column filters</button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="inv-pager">
                <div className="inv-pager-info">
                  {total === 0 ? "0" : `${pageStart + 1}–${Math.min(pageStart + PAGE_SIZE, total)}`} of {total}
                  <label className="inv-pagesize">Show
                    <select className="inp" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                      {[75, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                    per page
                  </label>
                </div>
                {total > 0 && (
                  <div className="inv-pager-ctrls">
                    <button className="pg-btn" disabled={pageIdx === 0} onClick={() => setPageIdx((p) => Math.max(0, p - 1))}>‹ Prev</button>
                    <span className="pg-cur">Page {pageIdx + 1} of {pageCount}</span>
                    <button className="pg-btn" disabled={pageIdx >= pageCount - 1} onClick={() => setPageIdx((p) => Math.min(pageCount - 1, p + 1))}>Next ›</button>
                  </div>
                )}
              </div>
            </div>
        )}
      </div>

      <SummaryBoard summary={summary} importedToday={importedToday} allocations={meta?.todayAllocations || []} groupLabel="all groups" statCount={meta?.statCount ?? 0} urgentCount={meta?.urgentCount ?? 0} untouchedCount={meta?.untouchedCount ?? 0} sheet={sheet}
        payorBreakdown={subView === "authnot" ? (meta?.payorBreakdown || []) : null} payorTotal={meta?.payorTotal ?? 0} />


      {menu && (
        <RecordMenu menu={menu} isSA={isSA} canStat={canStat} canDelete={canDelete} isDup={isDupTab} isArchiveTab={isArchiveTab} isRework={isReworkTab} sheet={sheet} assignees={assignees}
          count={selected.has(menu.record.id) && selected.size ? selected.size : 1}
          working={isWorking(groupOf(menu.record))} onClose={() => setMenu(null)}
          anyAssigned={menuRecords().some((r) => r.assignedUserName)}
          onUnassign={() => { const recs = menuRecords().filter((r) => r.assignedUserName); setMenu(null); if (recs.length) setUnassignReq({ ids: recs.map((r) => r.id), recs }); }}
          onRemoveArchive={() => act(() => api.rejectArchive(menuIds()), "Archive request withdrawn")}
          moved={!!menu.record.productionGroup} naturalGroup={derive(menu.record).location}
          onMoveBack={() => act(() => api.clearProduction(menuIds()), "Moved back to original group")}
          onView={(r) => { setMenu(null); setViewing(r); }}
          onAssign={(uid) => {
            const recs = menuRecords(); const busy = recs.filter((r) => r.assignedUserName && (r.status === "ASSIGNED" || r.status === "IN_PROGRESS"));
            const ids = menuIds();
            if (busy.length) { setMenu(null); setReassignReq({ uid, ids, busy }); }
            else { act(() => api.assignRecords(ids, uid), (res) => `Assigned ${res.assigned}`); }
          }}
          onMoveLacn={() => { const g = prodGroupFor(menuRecords()); act(() => api.sendToProduction(menuIds(), g), "Moved to production → " + g); }}
          onMove={(sh) => act(() => api.moveToSheet(menuIds(), sh), `Moved to ${SHEET_LABEL[sh]}`)}
          prioritized={!!menu.record.prioritized}
          onPrioritize={(on) => act(() => api.setPrioritized(menuIds(), on), on ? "Prioritized" : "Priority removed")}
          onStat={(on) => act(() => api.setRecordPriority(menuIds(), on ? "STAT" : null), on ? "Marked STAT" : "Cleared STAT")}
          onArchive={() => { const ids = menuIds(); setMenu(null); setArchiveReason(""); setArchiveReq({ ids }); }}
          onDelete={() => { const rec = menu.record; setMenu(null); setDelRec(rec); }}
          noPortal={!derive(menu.record).portal}
          onMapPortal={() => { const rec = menu.record; setMenu(null); setMapPortalRec(rec); }} />
      )}

      {assignOpen && (
        <AssignModal proc={proc} assignees={assignees} sheet={sheet}
          selectedRecords={selectedAll.length ? selectedAll : sheetRows.filter((r) => selected.has(r.id))}
          siblingsOf={(mrn, excludeId) => api.siblings(sheet, mrn, excludeId)}
          onClose={() => { setAssignOpen(false); setAssignTo(""); }}
          onAssign={(ids, uid) => { act(() => api.assignRecords(ids, Number(uid)), (r) => `Assigned ${r.assigned}`); setAssignOpen(false); setAssignTo(""); }} />
      )}

      {unassignReq && (
        <Modal title="De-assign records?" onClose={() => setUnassignReq(null)}
          foot={<><button className="btn btn-ghost" onClick={() => setUnassignReq(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => { const { ids } = unassignReq; setUnassignReq(null); act(() => api.unassignRecords(ids), (res) => `De-assigned ${res.assigned}`); }}>Yes, de-assign</button></>}>
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            {unassignReq.recs.length === 1
              ? <>PID <b>{unassignReq.recs[0].mrn}</b> is currently assigned to <b>{unassignReq.recs[0].assignedUserName}</b>. De-assigning will take it away from them and move it back to <b>To assign</b>, so it can be given to someone else.</>
              : <>These <b>{unassignReq.recs.length}</b> records will be taken away from the users they're assigned to and moved back to <b>To assign</b>, so they can be given to someone else.</>}
            <br />Do you want to de-assign?
          </p>
        </Modal>
      )}

      {reassignReq && (
        <Modal title="Already assigned" onClose={() => setReassignReq(null)}
          foot={<><button className="btn btn-ghost" onClick={() => setReassignReq(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => { const { ids, uid } = reassignReq; setReassignReq(null); act(() => api.assignRecords(ids, uid), (res) => `Re-assigned ${res.assigned}`); }}>Re-assign</button></>}>
          <div className="reassign-warn">
            {reassignReq.busy.length === 1
              ? <><b>{reassignReq.busy[0].assignedUserName}</b> is {reassignReq.busy[0].status === "IN_PROGRESS" ? "already working on" : "already assigned"} this record{reassignReq.busy[0].assignedByName ? <> — assigned by <b>{reassignReq.busy[0].assignedByName}</b></> : ""}.</>
              : <><b>{reassignReq.busy.length}</b> of these records are already assigned{reassignReq.busy.some((r) => r.status === "IN_PROGRESS") ? " (some in progress)" : ""}.{reassignReq.busy.some((r) => r.assignedByName) ? " Some were assigned by another admin." : ""}</>}
            <div className="reassign-sub">Re-assigning will move {reassignReq.busy.length === 1 ? "it" : "them"} to the new person.</div>
          </div>
        </Modal>
      )}

      {archiveReq && (
        <Modal title="Request archive?" sub={`${archiveReq.ids.length} record(s)`} onClose={() => setArchiveReq(null)}
          foot={<><button className="btn btn-ghost" onClick={() => setArchiveReq(null)}>Cancel</button>
            <button className="btn btn-danger" disabled={!archiveReason.trim()} onClick={() => act(() => api.requestArchive(archiveReq.ids, archiveReason).then((r) => { setArchiveReq(null); return r; }), "Archive requested")}>Yes, request archive</button></>}>
          <p style={{ margin: "0 0 14px", lineHeight: 1.6 }}>Are you sure you want to request archive for <b>{archiveReq.ids.length}</b> record(s)? They'll stay in their group, flagged as pending archive, until reviewed. You can withdraw the request anytime.</p>
          <div className="field"><label>Reason (required)</label>
            <textarea className="inp" rows={3} value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} placeholder="Why should this be archived?" /></div>
        </Modal>
      )}

      {delRec && (
        <Modal title="Delete duplicate" onClose={() => setDelRec(null)}
          foot={<><button className="btn btn-ghost" onClick={() => setDelRec(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => act(() => api.deleteRecords([delRec.id]).then((r) => { setDelRec(null); return r; }), "Duplicate deleted")}>Delete</button></>}>
          <p>Delete this duplicate — <b>PID {delRec.mrn}</b>, {delRec.orderName}? The other matching record stays.</p>
        </Modal>
      )}

      {viewing && <RecordModal record={viewing} onClose={() => setViewing(null)} isSA={isSA} canEdit={canStat} onEdit={(rec) => setEditRec(rec)} showHistory={isReworkTab} />}
      {conflicts && <ImportConflictModal conflicts={conflicts.list} onDone={confirmConflicts} />}
      {headerFilter === "portal" && (
        <HeaderFilterModal title="Filter by Portal" searchable searchPlaceholder="Search portals…"
          options={(meta?.portalOptions || []).map((o) => ({ value: o.portal, label: o.portal === "__NONE__" ? "(No portal)" : o.portal, count: o.count }))}
          selected={portalFilter} onClose={() => setHeaderFilter(null)}
          onApply={(sel) => { setPortalFilter(sel); setHeaderFilter(null); }} />
      )}
      {headerFilter === "rtcPriority" && (
        <HeaderFilterModal title="Filter by RTC priority"
          options={(meta?.rtcOptions || []).filter((o) => o.rtc != null && o.rtc >= 0).map((o) => ({ value: String(o.rtc), label: "RTC " + o.rtc, count: o.count }))}
          selected={rtcFilter} onClose={() => setHeaderFilter(null)}
          onApply={(sel) => { setRtcFilter(sel); setHeaderFilter(null); }} />
      )}
      {headerFilter === "assignedTo" && (
        <HeaderFilterModal title="Filter by Assigned To" searchable
          options={(meta?.assigneeOptions || []).map((o) => ({ value: o.id, label: o.id === "__NONE__" ? "Unassigned" : (o.casName ? `${o.casName} (${o.fullName})` : o.fullName), count: o.count }))}
          selected={assignFilter} onClose={() => setHeaderFilter(null)}
          onApply={(sel) => { setAssignFilter(sel); setHeaderFilter(null); }} />
      )}
      {headerFilter === "orders" && (
        <HeaderFilterModal title="Filter by Order" searchable searchPlaceholder="Search orders…"
          options={(meta?.orderOptions || []).map((o) => ({ value: o.order, label: o.order, count: o.count }))}
          selected={orderFilter} onClose={() => setHeaderFilter(null)}
          onApply={(sel) => { setOrderFilter(sel); setHeaderFilter(null); }} />
      )}
      {headerFilter === "orderMD" && (
        <HeaderFilterModal title="Filter by Order MD" searchable searchPlaceholder="Search order MDs…"
          options={(meta?.orderMdOptions || []).map((o) => ({ value: o.orderMd, label: o.orderMd, count: o.count }))}
          selected={orderMdFilter} onClose={() => setHeaderFilter(null)}
          onApply={(sel) => { setOrderMdFilter(sel); setHeaderFilter(null); }} />
      )}
      {headerFilter === "clinic" && (
        <HeaderFilterModal title="Filter by Clinic Location" searchable searchPlaceholder="Search locations…"
          options={(meta?.clinicOptions || []).map((o) => ({ value: o.clinic, label: o.code ? `${o.clinic} (${o.code})` : o.clinic, count: o.count }))}
          selected={clinicFilter} onClose={() => setHeaderFilter(null)}
          onApply={(sel) => { setClinicFilter(sel); setHeaderFilter(null); }} />
      )}
      {headerFilter === "paStatus" && (
        <HeaderFilterModal title="Filter by Status" searchable searchPlaceholder="Search statuses…"
          options={(meta?.statusOptions || []).map((o) => ({ value: o.status, label: o.status === "__NONE__" ? "Not worked" : o.status, count: o.count }))}
          selected={statusFilter} onClose={() => setHeaderFilter(null)}
          onApply={(sel) => { setStatusFilter(sel); setHeaderFilter(null); }} />
      )}
      {mapPortalRec && (
        <MapPortalModal record={mapPortalRec} portals={portalsList} onClose={() => setMapPortalRec(null)}
          onSaved={() => { setMapPortalRec(null); load(); }} toast={toast} />
      )}
      {uploadBlockMsg && (
        <Modal locked title="Wrong sheet for this process" onClose={() => setUploadBlockMsg("")}
          foot={<button className="btn btn-primary" onClick={() => setUploadBlockMsg("")}>OK, I'll rename it</button>}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "4px 2px" }}>
            <span style={{ flex: "0 0 auto", width: 34, height: 34, borderRadius: 8, background: "#fef2f2", color: "#b91c1c", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 20 }}>!</span>
            <div style={{ fontSize: 14, color: "#1f2733", lineHeight: 1.5 }}>{uploadBlockMsg}</div>
          </div>
        </Modal>
      )}
      {phiPrompt && (
        <Modal title="Before you upload" sub="Remove patient-identifying (PHI) columns from your sheet first."
          onClose={() => setPhiPrompt(false)}
          foot={<>
            <button className="btn btn-ghost" onClick={() => setPhiPrompt(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={proceedToFile}>I've removed them — choose file</button>
          </>}>
          <p style={{ margin: "0 0 12px", lineHeight: 1.6 }}>
            The upload will be blocked if the sheet contains patient identifiers. Please delete these columns from the raw sheet before uploading:
          </p>
          <div className="phi-list">
            <span className="phi-tag">First name</span>
            <span className="phi-tag">Last name</span>
            <span className="phi-tag">Middle name</span>
            <span className="phi-tag">Full name</span>
            <span className="phi-tag">Date of birth (DOB)</span>
            <span className="phi-tag">Patient phone number</span>
            <span className="phi-tag">Email</span>
            <span className="phi-tag">Address / street</span>
            <span className="phi-tag">ZIP / postal code</span>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Label a user as "CAS (Full name)" — CAS first, full name in brackets. Falls back gracefully.
const assigneeLabel = (u) => (u && u.casName ? (u.fullName && u.fullName !== u.casName ? `${u.casName} (${u.fullName})` : u.casName) : (u ? u.fullName : ""));

// Searchable single-select for assignees. Type to filter by CAS code or full name; the list is already
// restricted to production (USER-role) people by the server. Replaces the plain <select> so an admin can
// find a person by typing instead of scrolling.
function AssigneePicker({ list, value, onChange, placeholder }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = (list || []).find((u) => String(u.id) === String(value));
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const term = q.trim().toLowerCase();
  const filtered = term
    ? (list || []).filter((u) => (u.casName || "").toLowerCase().includes(term) || (u.fullName || "").toLowerCase().includes(term))
    : (list || []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input className="inp" placeholder={placeholder || "Search name or CAS…"}
        value={open ? q : (selected ? assigneeLabel(selected) : "")}
        onChange={(e) => { setQ(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setQ(""); setOpen(true); }} />
      {open && (
        <div style={{ position: "absolute", zIndex: 40, top: "calc(100% + 4px)", left: 0, right: 0, maxHeight: 240, overflow: "auto", background: "#fff", border: "1px solid #e5e9f0", borderRadius: 8, boxShadow: "0 8px 24px rgba(15,23,42,.12)" }}>
          {filtered.length === 0
            ? <div style={{ padding: "10px 12px", color: "#94a3b8", fontSize: 13 }}>No match.</div>
            : filtered.map((u) => (
                <button key={u.id} type="button"
                  onClick={() => { onChange(String(u.id)); setOpen(false); setQ(""); }}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", border: "none", borderBottom: "1px solid #f1f5f9", background: String(u.id) === String(value) ? "#eef2ff" : "transparent", cursor: "pointer", fontSize: 13 }}>
                  {assigneeLabel(u)}
                </button>
              ))}
        </div>
      )}
    </div>
  );
}

function AssignModal({ proc, assignees, sheet, selectedRecords, siblingsOf, onClose, onAssign }) {
  const single = selectedRecords.length === 1 ? selectedRecords[0] : null;
  const [siblings, setSiblings] = useState([]);
  // Fetch other same-MRN orders for EVERY selected record's MRN (not just when one is selected),
  // so siblings on other pages can be added too. Exclude anything already selected.
  const mrnKey = [...new Set(selectedRecords.map((r) => r.mrn).filter(Boolean))].sort().join(",");
  useEffect(() => {
    let alive = true;
    const mrns = [...new Set(selectedRecords.map((r) => r.mrn).filter(Boolean))];
    const selIds = new Set(selectedRecords.map((r) => r.id));
    Promise.all(mrns.map((mrn) => Promise.resolve(siblingsOf(mrn, -1)).catch(() => [])))
      .then((lists) => {
        if (!alive) return;
        const all = []; const seenId = new Set();
        lists.flat().forEach((s) => { if (s && !selIds.has(s.id) && !seenId.has(s.id)) { seenId.add(s.id); all.push(s); } });
        setSiblings(all);
      });
    return () => { alive = false; };
  }, [mrnKey]); // eslint-disable-line
  const [assignTo, setAssignTo] = useState("");
  // Group-based: only offer users who work for this record's client group.
  const recGroup = (() => { const gs = [...new Set(selectedRecords.map((r) => r.productionGroup || (derive(r) || {}).location).filter(Boolean))]; return gs.length === 1 ? gs[0] : null; })();
  const [groupAssignees, setGroupAssignees] = useState(null);
  useEffect(() => {
    let alive = true;
    Promise.resolve(api.listAssignees(sheet, recGroup)).then((a) => { if (alive) setGroupAssignees(Array.isArray(a) ? a : null); }).catch(() => { if (alive) setGroupAssignees(null); });
    return () => { alive = false; };
  }, [recGroup, sheet]); // eslint-disable-line
  const assigneeList = groupAssignees || assignees;
  const [also, setAlso] = useState(() => new Set());
  const toggle = (id) => setAlso((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // collapse identical orders (same order + same dates) so the list shows distinct orders only
  const sibDistinct = useMemo(() => {
    const seen = new Set(); const out = [];
    siblings.forEach((s) => {
      const o = (s.orderName || "").toLowerCase();
      // Refer-to-Physician / Specialty-Services copies are legitimately multiple (same order + dates): show EACH
      // one so the admin can select and assign all of them, not a single collapsed row that hides the rest.
      if (o.includes("refer to physician") || o.includes("specialty services")) { out.push(s); return; }
      const d = derive(s) || {};
      const key = `${o}|${d.orderDate || ""}|${d.rtcDate || ""}`;
      if (seen.has(key)) return; seen.add(key); out.push(s);
    });
    return out;
  }, [siblings]);
  const allSibSelected = sibDistinct.length > 0 && sibDistinct.every((s) => also.has(s.id));
  const toggleAllSib = () => setAlso((p) => { const n = new Set(p); if (allSibSelected) sibDistinct.forEach((s) => n.delete(s.id)); else sibDistinct.forEach((s) => n.add(s.id)); return n; });

  // A record only counts as "already assigned" if it has an assignee AND is in an assigned/working status.
  const busyRecords = selectedRecords.filter((r) => r.assignedUserName && (r.status === "ASSIGNED" || r.status === "IN_PROGRESS"));
  const [confirmed, setConfirmed] = useState(busyRecords.length === 0);

  // Always assign EVERY selected record (across pages) plus any ticked siblings.
  const ids = [...new Set([...selectedRecords.map((r) => r.id), ...also])];
  const count = ids.length;
  // #2: Refer-to-Physician / Specialty-Services selections — recommend single-user allocation.
  const isReferSel = selectedRecords.some((r) => { const o = (r.orderName || "").toLowerCase(); return o.includes("refer to physician") || o.includes("specialty services"); });

  if (!confirmed) {
    const inProg = busyRecords.filter((r) => r.status === "IN_PROGRESS");
    return (
      <Modal title="Already assigned" onClose={onClose}
        foot={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={() => setConfirmed(true)}>Re-assign</button></>}>
        <div className="reassign-warn">
          {busyRecords.length === 1
            ? <><b>{busyRecords[0].assignedUserName}</b> is {busyRecords[0].status === "IN_PROGRESS" ? "already working on" : "already assigned"} this record.</>
            : <><b>{busyRecords.length}</b> of the selected records are already assigned{inProg.length ? ` (${inProg.length} in progress)` : ""}.</>}
          <div className="reassign-sub">Re-assigning will move {busyRecords.length === 1 ? "it" : "them"} to a different person. Continue?</div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal wide={sibDistinct.length > 0} title={`Assign ${count} record(s)`} sub={`To a ${proc}`} onClose={onClose}
      foot={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={!assignTo} onClick={() => onAssign(ids, assignTo)}>Assign {count}</button></>}>
      {isReferSel && (
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#b91c1c", borderRadius: 8, padding: "10px 12px", marginBottom: 12, fontWeight: 600, fontSize: 13, lineHeight: 1.5 }}>
          ⚠ Refer-to-Physician / Specialty Services: please assign all copies of the same Unique ID to a <b>single user</b> so they can coordinate. If you split them across users, make sure that’s intended and coordinate with the team.
        </div>
      )}
      {assignees.length === 0
        ? <p className="muted">No active {proc}s — add one in User management first.</p>
        : <>
            <div className="field"><label>Assign to a {proc}</label>
              <AssigneePicker list={assigneeList} value={assignTo} onChange={setAssignTo}
                placeholder={`Search ${proc} by CAS or name…`} /></div>

            {selectedRecords.length > 1 && (
              <div className="mrn-sib-note" style={{ marginBottom: 8 }}>Assigning <b>{selectedRecords.length}</b> selected record(s){sibDistinct.length ? <> — plus you can add related orders below.</> : "."}</div>
            )}

            {sibDistinct.length > 0 && (
              <div className="mrn-sib">
                <div className="mrn-sib-note">These patients have other orders (some may be on other pages). Tick the ones you also want to give this person.
                  {sibDistinct.length > 1 && <button type="button" className="link-btn" style={{ marginLeft: 8, fontWeight: 700 }} onClick={toggleAllSib}>{allSibSelected ? "Clear all" : `Select all ${sibDistinct.length}`}</button>}
                </div>
                <div className="mrn-sib-list">
                  {selectedRecords.map((sr) => { const ds = derive(sr); const pr = ds.rtcPriority; return (
                    <label key={"sel" + sr.id} className="mrn-sib-row locked">
                      <input type="checkbox" checked readOnly />
                      <span className="sib-main">
                        <span className="sib-order">{sr.orderName || "—"} <span className="sib-thisone">selected · PID {sr.mrn}</span></span>
                        <span className="sib-meta">
                          <span className="sib-tag">Order:&nbsp;{ds.orderDate || "—"}</span>
                          <span className="sib-dash">–</span>
                          <span className="sib-tag">RTC:&nbsp;{ds.rtcDate || "—"}</span>
                          <span className="sib-dash">–</span>
                          <span className="sib-tag">RTC Priority:&nbsp;{pr === "" || pr == null ? "—" : pr}</span>
                        </span>
                      </span>
                    </label>
                  ); })}
                  {sibDistinct.map((s) => { const ds = derive(s); const pr = ds.rtcPriority; return (
                    <label key={s.id} className="mrn-sib-row">
                      <input type="checkbox" checked={also.has(s.id)} onChange={() => toggle(s.id)} />
                      <span className="sib-main">
                        <span className="sib-order">{s.orderName || "—"} <span className="sib-thisone" style={{ background: "#eef2ff", color: "#4f46e5" }}>PID {s.mrn}</span></span>
                        <span className="sib-meta">
                          <span className="sib-tag">Order:&nbsp;{ds.orderDate || "—"}</span>
                          <span className="sib-dash">–</span>
                          <span className="sib-tag">RTC:&nbsp;{ds.rtcDate || "—"}</span>
                          <span className="sib-dash">–</span>
                          <span className="sib-tag">RTC Priority:&nbsp;{pr === "" || pr == null ? "—" : pr}</span>
                          {s.paStatus ? <><span className="sib-dash">–</span><span className="sib-tag">{s.paStatus}</span></> : null}
                          {s.assignedUserName ? <><span className="sib-dash">–</span><span className="sib-tag">{s.assignedUserName}</span></> : null}
                        </span>
                      </span>
                    </label>
                  ); })}
                </div>
              </div>
            )}
          </>}
    </Modal>
  );
}

function RecordMenu({ menu, isSA, canStat, canDelete, isDup, isArchiveTab, isRework, moved, naturalGroup, prioritized, sheet, assignees, count, working, anyAssigned, onClose, onView, onAssign, onUnassign, onRemoveArchive, onMoveLacn, onMoveBack, onPrioritize, onStat, onArchive, onDelete, onMove, noPortal, onMapPortal }) {
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onScrollClose = (e) => { if (ref.current && ref.current.contains(e.target)) return; onClose(); };  // don't close when scrolling INSIDE the menu (e.g. the user list)
    document.addEventListener("mousedown", close); document.addEventListener("scroll", onScrollClose, true);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("scroll", onScrollClose, true); };
  }, [onClose]);
  const r = menu.record;
  const stat = derive(r).clinicPriority === "STAT";      // already STAT (by clinic or override)
  const overridden = r.priorityOverride === "STAT";        // STAT set by a super admin
  const proc = STAGE_PROC[sheet];
  const n = count > 1 ? ` (${count})` : "";
  const style = { top: Math.min(menu.y, window.innerHeight - 300), left: Math.min(menu.x, window.innerWidth - 230) };
  const isRequested = r.archiveStatus === "REQUESTED";
  if (isRequested) {
    return (
      <div className="ctx-menu" style={style} ref={ref}>
        <div className="ctx-empty">Archive pending — only a super admin can act on it.</div>
      </div>
    );
  }
  return (
    <div className="ctx-menu" style={style} ref={ref}>
      {(working || isRework) && (
        <div className="ctx-sub"><div className="ctx-item has-sub">Assign{n} to {proc} ▸
          <div className="ctx-flyout">
            {assignees.length === 0 ? <div className="ctx-empty">No active {proc}s</div>
              : assignees.map((u) => <button key={u.id} className="ctx-item" onClick={() => onAssign(u.id)}>{assigneeLabel(u)}</button>)}
          </div></div></div>
      )}
      {isRework && (<>
        <div className="ctx-div" />
        <button className="ctx-item" onClick={() => onMove("MODIFICATION")}>Move{n} to Modification Needed</button>
        <button className="ctx-item" onClick={() => onMove("MORE_INFO")}>Move{n} to More Info Needed</button>
        <div className="ctx-div" />
      </>)}
      {!working && !isRework && <button className="ctx-item" onClick={onMoveLacn}>Move{n} to production</button>}
      {anyAssigned && <button className="ctx-item" onClick={onUnassign}>De-assign{n}</button>}
      {moved && isWorking(groupOf(menu.record)) && naturalGroup && !isWorking(naturalGroup) && (
        <button className="ctx-item" onClick={onMoveBack}>Move back to {naturalGroup}</button>
      )}
      <button className="ctx-item" onClick={() => onPrioritize(!prioritized)}>{prioritized ? `Remove priority${n}` : `Prioritize${n} (send to top)`}</button>
      {canStat && (
        !stat ? <button className="ctx-item" onClick={() => onStat(true)}>Make STAT{n}</button>
        : overridden ? <button className="ctx-item" onClick={() => onStat(false)}>Clear STAT{n}</button>
        : null
      )}
      {noPortal && count <= 1 && (<>
        <div className="ctx-div" />
        <button className="ctx-item" onClick={onMapPortal}>Map portal…</button>
      </>)}
      <div className="ctx-div" />
      <button className="ctx-item" onClick={onArchive}>Request archive{n}</button>
      {canDelete && isDup && <button className="ctx-item danger" onClick={onDelete}>Delete duplicate</button>}
    </div>
  );
}

// Right-click "Map portal" on an empty-portal record → map its payor to a portal.
function MapPortalModal({ record, portals, onClose, onSaved, toast }) {
  const payor = (derive(record).insurance || "").trim();
  const [portal, setPortal] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!portal) return;
    setBusy(true);
    try {
      const res = await api.mapPortal({ payor, portal });
      toast(`Mapped ${payor} → ${portal}${res?.updated ? ` (${res.updated} record${res.updated === 1 ? "" : "s"} updated)` : ""}`, "ok");
      onSaved();
    } catch (e) { toast(e.message, "err"); setBusy(false); }
  };
  return (
    <Modal title="Map portal" sub="This record has no portal — map its payor to a portal." onClose={onClose}
      foot={<>
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy || !portal || !payor}>{busy ? "Saving…" : "Save mapping"}</button>
      </>}>
      <div className="field" style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: "#334155", display: "block", marginBottom: 5 }}>Payor / IPA</label>
        <div className="inp" style={{ background: "#f8fafc", color: payor ? "#1f2733" : "#b91c1c", display: "flex", alignItems: "center", height: 40 }}>
          {payor || "This record has no payor — nothing to map."}
        </div>
      </div>
      <div className="field">
        <label style={{ fontSize: 12, fontWeight: 700, color: "#334155", display: "block", marginBottom: 5 }}>Portal</label>
        <select className="inp" style={{ width: "100%", height: 40 }} value={portal} onChange={(e) => setPortal(e.target.value)}>
          <option value="">Select a portal…</option>
          {portals.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
        </select>
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
        Saving maps <b>every</b> record with this payor to this portal, now and on future imports.
      </div>
    </Modal>
  );
}

// Column-header filter dialog: searchable (optional), multi-select with counts, Apply / Clear.
function HeaderFilterModal({ title, searchable, searchPlaceholder, options, selected, onApply, onClose }) {
  const [sel, setSel] = useState(new Set(selected));
  const [q, setQ] = useState("");
  // Freeze the first non-empty options list so a background meta refresh can't blank the dialog mid-use.
  const frozen = useRef(null);
  if (frozen.current === null && options && options.length) frozen.current = options;
  const opts = frozen.current || options || [];
  const toggle = (v) => setSel((p) => { const n = new Set(p); n.has(v) ? n.delete(v) : n.add(v); return n; });
  const shown = searchable && q.trim()
    ? opts.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()))
    : opts;
  const allShownSelected = shown.length > 0 && shown.every((o) => sel.has(o.value));
  const toggleAllShown = () => setSel((p) => {
    const n = new Set(p);
    if (allShownSelected) shown.forEach((o) => n.delete(o.value));
    else shown.forEach((o) => n.add(o.value));
    return n;
  });
  return (
    <Modal title={title} onClose={onClose}
      foot={<>
        <button className="btn btn-ghost" onClick={() => { setSel(new Set()); }}>Clear all</button>
        <button className="btn btn-primary" onClick={() => onApply([...sel])}>Apply{sel.size ? ` (${sel.size})` : ""}</button>
      </>}>
      {searchable && (
        <input className="inp" autoFocus placeholder={searchPlaceholder || "Search…"} value={q} onChange={(e) => setQ(e.target.value)}
          style={{ width: "100%", height: 38, marginBottom: 10 }} />
      )}
      <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid #e5e9f0", borderRadius: 10 }}>
        {shown.length === 0
          ? <div style={{ padding: 16, color: "#94a3b8", fontSize: 13, textAlign: "center" }}>No options.</div>
          : <>
              <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid #eef1f6", fontWeight: 700, cursor: "pointer" }}>
                <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown} />
                <span>Select all{searchable && q.trim() ? " shown" : ""}</span>
              </label>
              {shown.map((o) => (
                <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid #f4f6fa", cursor: "pointer" }}>
                  <input type="checkbox" checked={sel.has(o.value)} onChange={() => toggle(o.value)} />
                  <span style={{ flex: 1 }}>{o.label}</span>
                  <span style={{ color: "#94a3b8", fontSize: 12, fontWeight: 700 }}>{o.count}</span>
                </label>
              ))}
            </>}
      </div>
    </Modal>
  );
}

function SummaryBoard({ summary, importedToday, allocations = [], groupLabel = "all groups", statCount = 0, urgentCount = 0, untouchedCount = 0, sheet = "SUBMISSION", payorBreakdown = null, payorTotal = 0 }) {
  const [dayUser, setDayUser] = useState(null);   // {id, name} whose today's-work detail is open
  const row = (label) => {
    const o = summary[label]; const total = o.open + o.done; const pct = total ? Math.round((o.done / total) * 100) : 0;
    return (
      <div className="sum-row" key={label}>
        <div className="sum-top"><span className="sum-label">{label}</span><span className="sum-open">{o.open} left</span></div>
        <div className="sum-bar"><div className="sum-fill" style={{ width: pct + "%" }} /></div>
        <div className="sum-sub">{o.done} done of {total}</div>
      </div>
    );
  };
  const totalToday = allocations.reduce((s, a) => s + (a.count || 0), 0);
  return (
    <>
    <div className="summary-col">
    <aside className="summary-board">
      <div className="summary-title"><Icon.home width="16" height="16" />Live summary</div>
      <div className="sum-chips">
        <span className="sum-chip sum-chip-stat">STAT <b>{statCount}</b></span>
        <span className="sum-chip sum-chip-urg">Urgent <b>{urgentCount}</b></span>
      </div>
      {TYPES.map((t) => row(t))}
      {importedToday && (
        <div className="import-done-card"><Icon.check width="15" height="15" /><span>Today's import is done</span></div>
      )}
    </aside>
    {payorBreakdown ? (
    <aside className="alloc-board">
      <div className="summary-title"><Icon.filter width="16" height="16" />Auth Not Needed by payor <span className="alloc-scope">· {payorTotal} total</span></div>
      {payorBreakdown.length === 0
        ? <div className="alloc-empty">No Auth-Not-Needed records.</div>
        : <div className="table-wrap" style={{ maxHeight: "calc(100vh - 300px)", minHeight: 160, overflowY: "auto", overflowX: "hidden" }}>
            <table className="tbl alloc-tbl" style={{ width: "100%", tableLayout: "fixed", fontSize: 11.5 }}>
              <colgroup><col style={{ width: "78%" }} /><col style={{ width: "22%" }} /></colgroup>
              <thead><tr><th style={{ textAlign: "left", padding: "5px 6px" }}>Payor</th><th style={{ textAlign: "center", padding: "5px 4px" }}>Count</th></tr></thead>
              <tbody>
                {payorBreakdown.map((p) => (
                  <tr key={p.payor}>
                    <td style={{ padding: "5px 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.payor}>{p.payor}</td>
                    <td style={{ textAlign: "center", padding: "5px 4px", fontWeight: 700 }}>{p.count}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr style={{ fontWeight: 800, borderTop: "2px solid #e2e8f0" }}><td style={{ padding: "5px 6px" }}>Total</td><td style={{ textAlign: "center", padding: "5px 4px" }}>{payorTotal}</td></tr></tfoot>
            </table>
          </div>}
    </aside>
    ) : (
    <aside className="alloc-board">
      <div className="summary-title"><Icon.users width="16" height="16" />Workload <span className="alloc-scope">· {groupLabel}</span></div>
      {allocations.length === 0
        ? <div className="alloc-empty">Nothing sitting in anyone's queue.</div>
        : <div className="table-wrap" style={{ maxHeight: "calc(100vh - 430px)", minHeight: 160, overflowY: "auto", overflowX: "hidden" }}>
            <table className="tbl alloc-tbl" style={{ width: "100%", tableLayout: "fixed", fontSize: 11.5 }}>
              <colgroup>
                <col style={{ width: "40%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>
              <thead><tr>
                <th style={{ textAlign: "left", padding: "5px 6px" }}>User</th>
                <th style={{ textAlign: "center", padding: "5px 4px" }} title="Records still not worked from before today">Left</th>
                <th style={{ textAlign: "center", padding: "5px 4px" }} title="Records assigned today (click a number to see the day's detail)">Today</th>
                <th style={{ textAlign: "center", padding: "5px 4px" }} title="Records kept in Reviewed (drops as the status is changed)">Rev.</th>
              </tr></thead>
              <tbody>
                {allocations.map((a) => {
                  const issueOwner = a.user === "PHS-SY" || a.user === "PHS-AJ";
                  const lo = a.yesterdayLeft || 0, td = a.todayAssigned || 0, rv = a.reviewed || 0;
                  return (
                    <tr key={a.user}>
                      <td style={{ padding: "5px 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={(a.fullName || a.user) + (issueOwner ? " · handles known-issue records" : "")}>{a.user}{issueOwner && <span title="Handles the known-issue records" style={{ marginLeft: 4, color: "#dc2626", fontSize: 9, fontWeight: 800 }}>⚠</span>}</td>
                      <td style={{ textAlign: "center", padding: "5px 4px" }}>{lo}</td>
                      <td style={{ textAlign: "center", padding: "5px 4px" }}>{a.userId ? <button className="link-btn" style={{ fontWeight: 700 }} onClick={() => setDayUser({ id: a.userId, name: a.user })}>{td}</button> : td}</td>
                      <td style={{ textAlign: "center", padding: "5px 4px" }}>{rv}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr style={{ fontWeight: 800, borderTop: "2px solid #e2e8f0" }}>
                <td style={{ padding: "5px 6px" }}>Total</td>
                <td style={{ textAlign: "center", padding: "5px 4px" }}>{allocations.reduce((s2, a) => s2 + (a.yesterdayLeft || 0), 0)}</td>
                <td style={{ textAlign: "center", padding: "5px 4px" }}>{allocations.reduce((s2, a) => s2 + (a.todayAssigned || 0), 0)}</td>
                <td style={{ textAlign: "center", padding: "5px 4px" }}>{allocations.reduce((s2, a) => s2 + (a.reviewed || 0), 0)}</td>
              </tr></tfoot>
            </table>
          </div>}
    </aside>
    )}
    </div>
    {dayUser && <DayDetailModal sheet={sheet} user={dayUser} onClose={() => setDayUser(null)} />}
    </>
  );
}

/* Per-user detail for today's assigned records: how many assigned, how many worked / not worked, and what they worked with status. */
function DayDetailModal({ sheet, user, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let a = true; setRows(null);
    // Log-based: every record assigned to this user today, CONSTANT for the whole day (a worked / Pending / kept-in-
    // Reviewed / routed-on record still shows). Only a de-assign removes one.
    api.dayDetail({ sheet, userId: user.id })
      .then((r) => { if (a) setRows(r || []); }).catch((e) => { if (a) { setErr(e.message); setRows([]); } });
    return () => { a = false; };
  }, [sheet, user.id]);
  const isRev = (r) => r.paStatus === "Reviewed" || !!r.reviewedSince;
  // "Worked" = records this user actually completed today (same source as the Allocations "Completed" column),
  // not just records that happen to carry a status — so the two counts always agree.
  const worked = (rows || []).filter((r) => r.completedToday);
  const notWorked = (rows || []).filter((r) => !r.completedToday);
  const reviewedKept = (rows || []).filter(isRev);
  const statusChip = (r) => {
    if (isRev(r)) return <span style={{ fontWeight: 700, color: "#1D4ED8" }}>Reviewed</span>;
    if (r.paStatus && r.paStatus.trim()) return <span style={{ fontWeight: 700, color: STATUS_COLOR[r.paStatus] || "#0f172a" }}>{r.paStatus}</span>;
    return <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Not worked</span>;
  };
  // Which group(s) and process this user's assigned records fall under — the workload container lists users across
  // every group, so this tells the admin where these records actually sit.
  const PROC_LABEL = { SUBMISSION: "Submission", END_PROCESS: "End Process", MODIFICATION: "Modification", MORE_INFO: "More Info" };
  const groupsIn = [...new Set((rows || []).map((r) => r.productionGroup || derive(r).location).filter(Boolean))];
  const scopeText = [groupsIn.join(", "), PROC_LABEL[sheet] || sheet || ""].filter(Boolean).join(" · ");
  return (
    <Modal title={`${user.name} — today's work`} onClose={onClose}>
      {rows === null ? <div className="empty" style={{ padding: "44px 0", display: "grid", placeItems: "center" }}><SwitchSpinner /></div>
        : err ? <div className="err-text">{err}</div>
        : <>
            <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ background: "#eef2ff", color: "#4f46e5", fontWeight: 800, borderRadius: 0, padding: "3px 11px", fontSize: 12 }}>Assigned: {rows.length}</span>
              <span style={{ background: "#ecfdf5", color: "#065f46", fontWeight: 800, borderRadius: 0, padding: "3px 11px", fontSize: 12 }}>Worked: {worked.length}</span>
              <span style={{ background: "#eff6ff", color: "#1D4ED8", fontWeight: 800, borderRadius: 0, padding: "3px 11px", fontSize: 12 }}>Reviewed: {reviewedKept.length}</span>
              <span style={{ background: "#fef2f2", color: "#991b1b", fontWeight: 800, borderRadius: 0, padding: "3px 11px", fontSize: 12 }}>Not worked: {notWorked.length}</span>
              {scopeText && <span style={{ marginLeft: "auto", fontSize: 11, color: "#64748b", fontWeight: 600 }}>{scopeText}</span>}
            </div>
            {rows.length === 0 ? <p className="muted" style={{ margin: 0 }}>Nothing assigned today.</p>
              : <div className="table-wrap" style={{ maxHeight: "56vh", overflow: "auto" }}>
                  <table className="tbl"><thead><tr><th>PID</th><th>Unique ID</th><th>Status</th></tr></thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}><td className="mono">{r.mrn}</td><td className="wrap mono" style={{ fontSize: 12 }}>{r.uniqueId || "—"}</td>
                          <td>{statusChip(r)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>}
          </>}
    </Modal>
  );
}

/* Reviewed sub-view for a group — the records kept in Reviewed, in a grid that matches the records grid.
   Latest remark shows inline; clicking a row opens a dialog with all the daily remarks + key details. */
function ReviewedPane({ sheet, group, q = "", onCount }) {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(null);   // the row whose remarks dialog is open
  const [hf, setHf] = useState(null);       // which column filter dialog is open
  const [fClinic, setFClinic] = useState([]);
  const [fPortal, setFPortal] = useState([]);
  const [fUrgent, setFUrgent] = useState([]);
  const [fRtc, setFRtc] = useState([]);
  const [fKept, setFKept] = useState([]);
  const [sel, setSel] = useState(() => new Set());   // selected record ids (for re-assign)
  const [assignees, setAssignees] = useState([]);
  const [reOpen, setReOpen] = useState(false);       // re-assign modal
  const [reAssignTo, setReAssignTo] = useState("");  // chosen assignee in the re-assign dialog
  const load = useCallback(() => {
    let a = true; setRows(null);
    // scope to THIS process (sheet) + group so switching the group/process tab shows only that tab's reviewed records
    api.reviewedAging({ group, process: sheet }).then((r) => { if (a) { setRows(r || []); setErr(""); } }).catch((e) => { if (a) { setErr(e.message); setRows([]); } });
    return () => { a = false; };
  }, [group, sheet]);
  useEffect(() => { const c = load(); return c; }, [load]);
  useEffect(() => { if (rows && typeof onCount === "function") onCount(rows.length); }, [rows]); // eslint-disable-line
  useEffect(() => { api.listAssignees(sheet).then(setAssignees).catch(() => setAssignees([])); }, [sheet]);
  useEffect(() => { setFClinic([]); setFPortal([]); setFUrgent([]); setFRtc([]); setFKept([]); setSel(new Set()); }, [group, sheet]);
  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const doReassign = (uid) => {
    const ids = [...sel];
    api.assignRecords(ids, Number(uid)).then((r) => { toast(`Re-assigned ${r.assigned ?? ids.length}`, "ok"); setReOpen(false); setReAssignTo(""); setSel(new Set()); load(); })
      .catch((e) => toast(e.message, "err"));
  };
  const remarksOf = (r) => (r.checkins || []).filter((c) => c.comment && c.comment.trim());
  const keptCas = (r) => r.assignedUserCas || r.assignedUserName || "—";
  const portalOf = (r) => (r.portal == null ? "" : String(r.portal).trim());   // whitespace-only portal counts as no portal
  const urgentOf = (r) => (r.urgentRoutine === "Urgent" ? "Urgent" : "Routine");
  const rtcOf = (r) => (r.rtcPriority == null || r.rtcPriority === "" ? "" : String(r.rtcPriority));
  // filter option lists computed from the loaded rows (client-side, since Reviewed loads the whole set)
  const optsOf = (getter) => { const m = new Map(); (rows || []).forEach((r) => { const v = getter(r); if (v == null || v === "") return; m.set(String(v), (m.get(String(v)) || 0) + 1); }); return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, label: value, count })); };
  // Portal options with an explicit "(No portal)" bucket (value "") so records without a portal can be filtered.
  const portalOptions = () => {
    const arr = optsOf(portalOf);
    const none = (rows || []).filter((r) => portalOf(r) === "").length;
    if (none > 0) arr.unshift({ value: "", label: "(No portal)", count: none });
    return arr;
  };
  // Kept-by options: value is the CAS name (what the cell shows), label adds the full name in brackets.
  const keptOptions = () => {
    const m = new Map();
    (rows || []).forEach((r) => { const v = keptCas(r); if (!v || v === "—") return; const full = r.assignedUserName || ""; const e = m.get(v) || { count: 0, full }; e.count++; if (!e.full) e.full = full; m.set(v, e); });
    return [...m.entries()].sort((a, b) => b[1].count - a[1].count).map(([value, o]) => ({ value, label: o.full && o.full !== value ? `${value} (${o.full})` : value, count: o.count }));
  };
  const filtered = useMemo(() => {
    const t = (q || "").trim().toLowerCase();
    return (rows || []).filter((r) =>
      (!fClinic.length || fClinic.includes(r.clinic || "")) &&
      (!fPortal.length || fPortal.includes(portalOf(r))) &&
      (!fUrgent.length || fUrgent.includes(urgentOf(r))) &&
      (!fRtc.length || fRtc.includes(rtcOf(r))) &&
      (!fKept.length || fKept.includes(keptCas(r))) &&
      (!t || [r.mrn, r.orderName, r.clinic, r.portal, r.reason, r.reviewFutureDate, r.assignedUserName, r.assignedUserCas, r.lastComment]
        .some((v) => String(v || "").toLowerCase().includes(t)))
    );
  }, [rows, fClinic, fPortal, fUrgent, fRtc, fKept, q]); // eslint-disable-line
  if (rows === null) return <div className="empty"><Spinner /></div>;
  if (err) return <div className="err-text" style={{ padding: 18 }}>{err}</div>;
  const th = (label, key, active) => (
    <th className={"th-sort th-filter" + (active.length ? " th-filter-on" : "")} onClick={(e) => { e.stopPropagation(); setHf(hf === key ? null : key); }} title={`Filter by ${label}`}
      style={{ overflow: "visible", whiteSpace: "nowrap" }}>
      {label}<span style={{ marginLeft: 4, fontSize: 11, opacity: active.length ? 1 : 0.55 }}>▾</span>
      {active.length > 0 && <span style={{ marginLeft: 4, background: "#4f46e5", color: "#fff", borderRadius: 999, padding: "0 6px", fontSize: 10.5, fontWeight: 800 }}>{active.length}</span>}
    </th>
  );
  return (
    <div className="panel" style={{ paddingTop: 0 }}>
      {sel.size > 0 && (
        <div className="sel-fab">
          <span className="sel-count">{sel.size} selected</span>
          <button className="link-btn" onClick={() => setSel(new Set())}>Clear</button>
          <div className="sel-sep" />
          <button className="btn btn-primary btn-sm" onClick={() => { setReAssignTo(""); setReOpen(true); }}>Re-assign…</button>
        </div>
      )}
      <div className="table-wrap no-scroll">
        {rows.length === 0
          ? <Empty icon={<Icon.history width="40" height="40" />} title="Nothing in Reviewed" text="Records kept in Reviewed for this group will show here." />
          : <table className="tbl select-tbl rev-tbl">
              <colgroup>
                <col style={{ width: 46 }} />
                <col style={{ width: "7%" }} /><col style={{ width: "12%" }} /><col style={{ width: "15%" }} />
                <col style={{ width: "7%" }} /><col style={{ width: "11%" }} /><col style={{ width: "5%" }} />
                <col style={{ width: "12%" }} /><col style={{ width: "9%" }} /><col style={{ width: "9%" }} /><col style={{ width: "13%" }} />
              </colgroup>
              <thead><tr>
                <th className="col-check" />
                <th>PID (MRN)</th>{th("Clinic", "clinic", fClinic)}<th>Order</th>{th("Urgent", "urgent", fUrgent)}{th("Portal", "portal", fPortal)}{th("RTC", "rtc", fRtc)}
                <th>Reason</th><th>Future date</th>{th("Kept by", "kept", fKept)}<th>Latest remark</th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className={"row-click" + (sel.has(r.id) ? " selected" : "")} onClick={() => setOpen(r)} title="Open — see all remarks">
                    <td className="col-check" onClick={(e) => e.stopPropagation()}>
                      <label className="cbx"><input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleSel(r.id)} /><span className="box"><Icon.check /></span></label>
                    </td>
                    <td className="mono">{r.mrn}</td>
                    <td className="cell-sub wrap" title={r.clinic || ""}>{r.clinic || "—"}</td>
                    <td className="wrap" title={r.orderName || ""}>{r.orderName || "—"}</td>
                    <td className="nowrap">{r.urgentRoutine === "Urgent" ? <span style={{ color: "#EA580C", fontWeight: 700 }}>Urgent</span> : "Routine"}</td>
                    <td className="cell-sub wrap" title={r.portal || ""}>{r.portal || "—"}</td>
                    <td className="nowrap">{r.rtcPriority == null || r.rtcPriority === "" ? "—" : r.rtcPriority}</td>
                    <td className="wrap" title={r.reason || ""}>{r.reason || "—"}</td>
                    <td className="nowrap">{r.reviewFutureDate ? <span style={{ color: "#1D4ED8", fontWeight: 700 }}>{r.reviewFutureDate}</span> : "—"}</td>
                    <td className="nowrap" title={r.assignedUserName || ""}>{keptCas(r)}</td>
                    <td className="wrap" title={r.lastComment || ""}>{r.lastComment ? r.lastComment : <span className="muted">— no remark yet —</span>}</td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={11} style={{ textAlign: "center", color: "#64748b", padding: "28px 16px" }}>No records match the filters.</td></tr>}
              </tbody>
            </table>}
      </div>
      {rows && rows.length > 0 && (
        <div className="rev-count">
          Showing <b>{filtered.length}</b> of <b>{rows.length}</b> kept-in-Reviewed record{rows.length === 1 ? "" : "s"}
          {fKept.length ? <> · <b>{fKept.join(", ")}</b></> : null}
        </div>
      )}
      {open && (
        <RecordModal record={open} reviewRemarks={remarksOf(open)} isSA={false} canEdit={false} onClose={() => setOpen(null)} />
      )}
      {hf === "clinic" && <HeaderFilterModal title="Filter by Clinic Location" searchable options={optsOf((r) => r.clinic)} selected={fClinic} onClose={() => setHf(null)} onApply={(s) => { setFClinic(s); setHf(null); }} />}
      {hf === "portal" && <HeaderFilterModal title="Filter by Portal" searchable options={portalOptions()} selected={fPortal} onClose={() => setHf(null)} onApply={(s) => { setFPortal(s); setHf(null); }} />}
      {hf === "urgent" && <HeaderFilterModal title="Filter by Urgent" options={optsOf(urgentOf)} selected={fUrgent} onClose={() => setHf(null)} onApply={(s) => { setFUrgent(s); setHf(null); }} />}
      {hf === "rtc" && <HeaderFilterModal title="Filter by RTC" options={optsOf(rtcOf).map((o) => ({ ...o, label: "RTC " + o.value }))} selected={fRtc} onClose={() => setHf(null)} onApply={(s) => { setFRtc(s); setHf(null); }} />}
      {hf === "kept" && <HeaderFilterModal title="Filter by Kept reviewed by" searchable options={keptOptions()} selected={fKept} onClose={() => setHf(null)} onApply={(s) => { setFKept(s); setHf(null); }} />}
      {reOpen && (
        <Modal title={`Re-assign ${sel.size} record${sel.size === 1 ? "" : "s"}`} sub="To a reviewer" onClose={() => { setReOpen(false); setReAssignTo(""); }}
          foot={<><button className="btn btn-ghost" onClick={() => { setReOpen(false); setReAssignTo(""); }}>Cancel</button>
            <button className="btn btn-primary" disabled={!reAssignTo} onClick={() => doReassign(reAssignTo)}>Re-assign {sel.size}</button></>}>
          {assignees.length === 0
            ? <p className="muted">No users available for this group — add one in User management first.</p>
            : <div className="field">
                <label>Assign to</label>
                <select className="inp" value={reAssignTo} onChange={(e) => setReAssignTo(e.target.value)}>
                  <option value="">Select a person…</option>
                  {assignees.map((u) => <option key={u.id} value={u.id}>{u.casName ? `${u.casName} (${u.fullName})` : u.fullName}</option>)}
                </select>
              </div>}
        </Modal>
      )}
    </div>
  );
}

export function RecordModal({ record, onClose, isSA, canEdit, onEdit, reviewRemarks, showHistory, mine }) {
  const [full, setFull] = useState(record);
  // `mine` (My Work): show the record as THIS user worked it (their own round), not the live/latest values.
  useEffect(() => { let ok = true; (mine ? api.getRecordMine(record.id) : api.getRecord(record.id)).then((r) => { if (ok && r) setFull(r); }).catch(() => {}); return () => { ok = false; }; }, [record.id, mine]);
  // Reworked records were worked before (then flipped back to "auth needed" in Onco EMR) — pull the full
  // work-history so the admin can see everything that was already done on this request.
  const [priorRounds, setPriorRounds] = useState(null);
  useEffect(() => {
    if (!showHistory) { setPriorRounds(null); return; }
    let ok = true; setPriorRounds(null);
    api.priorWork(record.id).then((rows) => { if (ok) setPriorRounds(Array.isArray(rows) ? rows : []); }).catch(() => { if (ok) setPriorRounds([]); });
    return () => { ok = false; };
  }, [record.id, showHistory]);
  // Guideline(s) the worker(s) used on this record — shown in the detail everywhere this modal appears.
  const [glUsage, setGlUsage] = useState([]);
  useEffect(() => { if (!GUIDELINES_ENABLED) return; let ok = true; api.guidelineUsageList(record.id).then((rows) => { if (ok) setGlUsage(Array.isArray(rows) ? rows : []); }).catch(() => {}); return () => { ok = false; }; }, [record.id]);
  // glUsage is newest-first; show the latest guideline actually used (single), not every code copied while deciding.
  const glIdDisplay = (() => {
    if (!glUsage.length) return "—";
    const latest = glUsage.find((u) => u.guidelineId && u.guidelineId !== "NONE");
    return latest ? latest.guidelineId : "No guideline (reported)";
  })();

  const rec = full;
  // Submitter name is stored inconsistently across records — sometimes the CAS code (picked from the
  // End Process dropdown), sometimes the person's full name (the default fallback). Resolve whichever
  // was stored back to a person using the submitter group's CAS directory, and always display it as
  // "Full name (CAS)". Falls back to the raw stored value when it can't be matched.
  const [subDir, setSubDir] = useState([]);   // [{cas, full}] for rec.submitterGroup
  useEffect(() => {
    const g = rec.submitterGroup;
    if (!g || !rec.submitterName || !api.casNames) { setSubDir([]); return; }
    let ok = true;
    Promise.resolve(api.casNames(g)).then((n) => { if (ok) setSubDir(Array.isArray(n) ? n : []); }).catch(() => { if (ok) setSubDir([]); });
    return () => { ok = false; };
  }, [rec.submitterGroup, rec.submitterName]);
  const submitterDisplay = (() => {
    const raw = (rec.submitterName || "").trim();
    if (!raw) return "—";
    const lc = raw.toLowerCase();
    const hit = subDir.find((p) => (p.cas || "").toLowerCase() === lc || (p.full || "").toLowerCase() === lc);
    if (!hit) return raw;                                   // unmatched free text → show as-is
    const full = (hit.full || "").trim(), cas = (hit.cas || "").trim();
    if (full && cas && full.toLowerCase() !== cas.toLowerCase()) return `${full} (${cas})`;
    return full || cas || raw;
  })();
  const d = { ...derive(rec), assignedTo: rec.assignedUserName || "" };
  const grp = rec.productionGroup || d.location;
  const naturalStat = /asap|stat/i.test(String(raw(rec, "sDate") || ""));
  const overrodeToStat = rec.priorityOverride === "STAT" && !naturalStat;
  const clinicPriority = overrodeToStat
    ? <span className="cp-override"><s>Non-STAT</s><b className="cp-stat">STAT</b></span>
    : d.clinicPriority;
  const canEditManual = !!canEdit && rec.manualEntry;

  const fields = [
    ["PID", rec.mrn], ["Unique ID", rec.uniqueId], ["Clinic Location", d.clinic], ["Location Code", d.locationCode],
    ["Group", grp], ["Order", d.orders], ["OrderMD", d.orderMD], ["Order Date", d.orderDate],
    ["RTC Date", d.rtcDate], ["RTC Priority", d.rtcPriority], ["Urgent / Routine", d.urgentRoutine], ["Clinic Priority", clinicPriority],
    ["Payor / IPA", d.insurance], ["Portal", d.portal], ["Value (sValue)", raw(rec, "sValue")], ["Assigned To", d.assignedTo || "—"],
    ["Assigned On", rec.assignedAt ? new Date(rec.assignedAt).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "—"],
    ["Submitted via", rec.submissionMethod], ["Submitter group", rec.submitterGroup], ["Submitter name", submitterDisplay],
    [canEdit ? "Submitted CPTs" : "CPTs", rec.submittedCpts], [canEdit ? "Submitted ICDs" : "ICDs", rec.submittedIcds], ["Requests", (rec.requests || []).join(" | ")],
    ...(GUIDELINES_ENABLED ? [["Guideline ID", glIdDisplay]] : []),
    ["Status", rec.paStatus], ["Inbox", rec.inboxValue], ["Authorization Number", rec.authNumber], ["Request Number", rec.requestNumber],
    ["Fax", rec.faxNumber], ["Phone", rec.phoneNumber], ["Reason", rec.reason], ["Need Help", rec.needHelp],
    ["Future date (inbox)", rec.reviewFutureDate],
    ["Reason for Help", rec.reasonForHelp], ["Remarks", rec.remarks || rec.uploaderNote], ["Requested By", rec.requestedBy], ["Date Requested", rec.dateRequested],
  ];
  const manualSub = rec.manualEntry
    ? `Manually entered${rec.createdAt ? " on " + new Date(rec.createdAt).toLocaleString() : ""}${raw(rec, "__createdBy") ? " by " + raw(rec, "__createdBy") : ""}`
    : rec.uniqueId;

  const reviewDays = (() => {
    if (rec.paStatus !== "Reviewed" || !rec.reviewedSince) return null;
    return Math.max(0, Math.floor((Date.now() - new Date(rec.reviewedSince).getTime()) / 86400000));
  })();
  const hd = (t) => { try { if (!t) return ""; const d = new Date(t); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()}`; } catch { return ""; } };

  return (
    <Modal wide title={`PID ${rec.mrn}`} sub={manualSub} onClose={onClose}
      foot={canEditManual ? <button className="btn btn-primary" onClick={() => { onClose(); onEdit && onEdit(rec); }}>Edit</button> : undefined}>
      {reviewDays != null && (
        <div className="rm-reviewed">Kept in <b>Reviewed</b> for <b>{reviewDays} {reviewDays === 1 ? "day" : "days"}</b>{rec.reviewedSince ? <> (since {hd(rec.reviewedSince)})</> : null}</div>
      )}
      <div className="fit-grid">
        {fields.filter(([, v]) => v !== "" && v != null && v !== "—").map(([k, v]) => <div className="fit-cell" key={k}><span>{k}</span><b>{v}</b></div>)}
      </div>
      {reviewRemarks && reviewRemarks.length > 0 && (
        <>
          <div style={{ fontWeight: 800, fontSize: 13.5, margin: "16px 0 8px", color: "#334155" }}>All remarks</div>
          <div className="table-wrap" style={{ maxHeight: "35vh", overflow: "auto" }}>
            {reviewRemarks.map((c, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "7px 2px", borderBottom: "1px dashed #e2e8f0", fontSize: 13 }}>
                <span style={{ flex: "0 0 96px", fontWeight: 800, color: "#334155" }}>{hd(c.commentedAt || c.openedAt)}</span>
                <span style={{ flex: 1, color: "#0f172a", whiteSpace: "pre-wrap" }}>{c.comment}</span>
                <span style={{ flex: "0 0 auto", color: "#64748b" }}>{c.userName || ""}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {showHistory && (
        <>
          <div style={{ fontWeight: 800, fontSize: 13.5, margin: "18px 0 8px", color: "#334155" }}>
            Previous work {priorRounds && priorRounds.length > 0 ? `(${priorRounds.length} round${priorRounds.length === 1 ? "" : "s"})` : ""}
          </div>
          {priorRounds == null
            ? <div style={{ color: "#94a3b8", fontSize: 13, padding: "6px 2px" }}>Loading history…</div>
            : priorRounds.length === 0
              ? <div style={{ color: "#94a3b8", fontSize: 13, padding: "6px 2px" }}>No earlier work recorded for this request.</div>
              : (
                <div className="table-wrap" style={{ maxHeight: "40vh", overflow: "auto" }}>
                  {priorRounds.map((h, i) => {
                    const chips = [
                      ["Stage", SHEET_LABEL[h.stage] || h.stage],
                      ["Status", h.status],
                      ["Status date", h.statusDate],
                      ["Inbox", h.inbox],
                      ["CPTs", h.cpts],
                      ["ICDs", h.icds],
                      ["Request #", h.requestNumber],
                      ["Auth #", h.authNumber],
                      ["Submitter", h.submitterName],
                      ["Reason", h.reason],
                      ["Remarks", h.remarks],
                      ["Issue", h.issueType],
                    ].filter(([, v]) => v !== "" && v != null && String(v) !== "null");
                    return (
                      <div key={i} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px", marginBottom: 8, background: "#f8fafc" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
                          <span style={{ fontWeight: 800, color: "#334155", fontSize: 12.5 }}>Round {i + 1}{h.date ? ` · ${h.date}` : ""}</span>
                          <span style={{ color: "#64748b", fontSize: 12.5 }}>{h.by || ""}</span>
                        </div>
                        <div className="fit-grid">
                          {chips.map(([k, v]) => <div className="fit-cell" key={k}><span>{k}</span><b>{String(v)}</b></div>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
        </>
      )}
    </Modal>
  );
}

function ImportConflictModal({ conflicts, onDone }) {
  const [pick, setPick] = useState(new Set());
  const fmt = (t) => { try { return t ? new Date(t).toLocaleString() : "—"; } catch { return "—"; } };
  const toggle = (uid) => setPick((p) => { const n = new Set(p); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  const allPicked = conflicts.length > 0 && conflicts.every((c) => pick.has(c.uniqueId));
  return (
    <Modal locked title="Already worked on these requests" sub={`${conflicts.length} imported record(s) match a request that was already worked. Choose which to work again.`}
      foot={<>
        <button className="btn btn-ghost" onClick={() => onDone([])}>Skip all — don't import</button>
        <button className="btn btn-primary" onClick={() => onDone([...pick])} disabled={!pick.size}>{pick.size ? `Work again on ${pick.size}` : "Work again"}</button>
      </>}>
      <div className="conf-head">
        <label className="conf-checkall"><input type="checkbox" checked={allPicked} onChange={() => setPick(allPicked ? new Set() : new Set(conflicts.map((c) => c.uniqueId)))} /> Select all</label>
        <span className="conf-note">Only the ones you tick will be imported and worked again. The rest stay as they were.</span>
      </div>
      <div className="conf-list">
        {conflicts.map((c) => (
          <label key={c.uniqueId} className={"conf-row" + (pick.has(c.uniqueId) ? " on" : "")}>
            <input type="checkbox" checked={pick.has(c.uniqueId)} onChange={() => toggle(c.uniqueId)} />
            <div className="conf-main">
              <div className="conf-title"><b>PID {c.mrn}</b> — {c.orderName}</div>
              <div className="conf-meta">
                <span>Order:&nbsp;{c.orderDate || "—"}</span><span className="conf-dash">–</span>
                <span>RTC:&nbsp;{c.rtcDate || "—"}</span>
              </div>
              <div className="conf-worked">
                Already <b>{c.paStatus || "worked"}</b> by <b>{c.workedByName || "a user"}</b> on <b>{fmt(c.workedAt)}</b>
              </div>
            </div>
          </label>
        ))}
      </div>
    </Modal>
  );
}